/**
 * @module tribuna
 * Tribuna Pública — feed agregador de rituales con visibilidad pública.
 *
 * FASE 4 · F1.3.d2. Migrado de localStorage a Supabase Postgres: el feed se
 * lee de `tribuna_feed` vía el RPC `feed_tribuna` (src/core/tribuna.js). Los
 * triggers `fn_on_ritual_creado` / `fn_on_reaccion_creada` pueblan
 * `tribuna_feed` (score baseline); el cron `job_mantenimiento` lo limpia.
 *
 * REGLA DEL BLOQUE: el HTML / CSS / layout NO se rediseña. Solo cambia la capa
 * de datos — las filas de Postgres se adaptan al shape legacy del HTML-gen
 * (patrón getPactoByGroup). renderTribunaArengas es código muerto confirmado
 * (TODO 3.12-followup) — queda verbatim.
 *
 * Reaccionar / guardar arenga requieren login + jugador; ver la tribuna no.
 */

import { getCurrentUser } from '../core/auth.js';
import { getNormalizedDB } from '../core/db.js';
import { getActiveGroupCode, getMembership } from '../core/groups.js';
import { navigate } from '../core/router.js';
import {
    getFeedTribuna, invalidarCacheTribuna, TRIBUNA_WINDOW, TRIBUNA_LANE_CARDS, rellenarHastaN,
    TRIBUNA_REFRESCO_MS, TRIBUNA_DESTACADOS,
    contarArengasTotal, contarCabalasActivas, contarPromesasActivas, contarReaccionesTotal,
    resolverRutaTribunaPublica, getCapArengasAnon,
} from '../core/tribuna.js';
import { puedeEscucharArengaAnon, registrarEscuchaAnon } from '../utils/cap-arengas-anon.js';
import { seleccionarVidriera, barajar } from '../core/tribuna-vidriera.js';
import { buildShareUrl } from '../core/storage-arengas.js';
import { showLoadingOverlay, hideLoadingOverlay } from '../ui/loading-overlay.js';
import { ensureBlobUrl, revokeBlobUrl } from '../core/audio-player.js';
import { computeReactionDelta, invertDeltas, applyReactionDelta, applyReactionToModel } from '../core/reaction-delta.js';
import { reaccionar, quitarReaccion, guardarArenga, quitarArengaGuardada, getArengasGuardadasDelUsuario, getMisReaccionesArengas, getMisAlPastoArengas, getMisReacciones, getMisAlPasto, puntosPorRitual, refrescarContadoresRitual } from '../core/rituales.js';
import { subscribirCambios, limpiarSuscripciones } from '../core/realtime.js';
import { renderActiveGroupBar, renderBottomNav } from '../ui/components.js';
import { renderPactoIcon } from '../ui/icons.js';
import { openShareModal } from '../ui/share-modal.js';
import { renderArengaCard } from '../ui/arenga-card.js';
import { appRoot } from '../utils/dom.js';
import { formatTimeAgo } from '../utils/time-ago.js';
import { buildTribunaSkeleton, buildTribunaCabalasSkeleton, buildTribunaPromesasSkeleton } from './tribuna-skeleton.js';
// Encabezado de la Tribuna Pública: SVG provisto por el dueño (Vite lo resuelve
// como URL de asset, fuera del bundle JS). Reemplaza banderas+título+subtítulo.
// Header rasterizado (6-jun): WebP (18.7KB) + PNG fallback (17.8KB) reemplazan
// al SVG de 1.42MB (mismo arte, aspect ratio 900×256). El .svg queda como fuente.
import tribunaHeaderWebp from '../assets/tribuna-publica-header.webp';
import tribunaHeaderPng from '../assets/tribuna-publica-header.png';

// Tipos de arenga — vocabulario canónico del Schema v1.8 (CHECK actualizado
// 27-may-2026, alineado a spec arengas v1.2 §2 e idéntico al de rituales.js).
// Proxy: key desconocida cae al primer tipo (vamos) — cubre el fallback legacy
// del HTML-gen `|| tribunaArengaTypes.vamos` sin tocarlo.
const ARENGA_TYPES_BASE = {
    vamos:       { icon: 'M9 18V5l9-2v13M9 9l9-2M9 18a2 2 0 1 1-2-2 2 2 0 0 1 2 2ZM18 16a2 2 0 1 1-2-2 2 2 0 0 1 2 2Z', label: '¡Vamos todavía!', color: '#2563eb' },
    opinion:     { icon: 'M8 14c1.8-3.2 3.3-4.7 5.2-4.7h1.9c1.3 0 2.4 1.1 2.4 2.4v.6h-3.2M8 14l-2.2 3.2M8 14h6.7c1.5 0 2.8 1.2 2.8 2.8V18H9.6M12.5 9.4V6.8c0-1.3 1-2.3 2.3-2.3h.8v4.8', label: 'Opinión de partido', color: '#38bdf8' },
    sufrimiento: { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7.1l3.1 2.25-1.2 3.65h-3.8L8.9 9.35 12 7.1ZM12 7.1V4.2M15.1 9.35l2.9-1.2M13.9 13l1.85 2.85M10.1 13l-1.85 2.85M8.9 9.35 6 8.15M6.2 16.5l2.05-.65M17.8 16.5l-2.05-.65M4.2 11.6 6 8.15M19.8 11.6 18 8.15M7.4 18.8l.85-2.95M16.6 18.8l-.85-2.95', label: 'Sufrimiento', color: '#8b5cf6' },
    humor:       { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z', label: 'Humor', color: '#f59e0b' },
    festejo:     { icon: 'M6 20l3-10 5 5-8 5ZM14 4l.4 2.4L17 7l-2.6.6L14 10l-.4-2.4L11 7l2.6-.6L14 4ZM18 12l.3 1.7L20 14l-1.7.3L18 16l-.3-1.7L16 14l1.7-.3L18 12ZM7 5l.3 1.7L9 7l-1.7.3L7 9l-.3-1.7L5 7l1.7-.3L7 5Z', label: 'Festejo/Fiesta', color: '#22c55e' },
    canticos:    { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z', label: 'Cánticos', color: '#4f46e5' },
    bronca:      { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9l2 1M16 9l-2 1M8.5 16c1.8-1.4 5.2-1.4 7 0M9 12h.1M15 12h.1', label: 'Bronca/Desilusión', color: '#991b1b' },
    declaracion: { icon: 'M5 6h14v9H9l-4 4V6ZM8 9h8M8 12h6M17 3l1 2M21 7l-2 1M7 3 6 2', label: 'Declaración', color: '#fbbf24' },
};

// FASE 3.17 · Bloque D — guard anti race-de-navegación de renderTribuna.
// El render aborta si el user salió de la vista mientras los await cargaban.
// Las rutas válidas son /tribuna (tribuna pública entera sin grupo),
// /tribuna/<codigo> (tribuna interna o visitante con grupo activo) y
// /t/<audio_id> (link viral compartido).
export const esRutaConRenderTribuna = (hash) => {
    if (!hash) return false;
    return hash === '#/tribuna' || hash.startsWith('#/tribuna/') || hash.startsWith('#/t/');
};

// Share de la tribuna entera (28-may-2026). Link público sin codigo de grupo
// que abre la tribuna pública para invitados. Helpers puros para testear.
export const buildShareTribunaParams = () => ({
    title: 'Compartir tribuna',
    shareUrl: 'https://pactomundial.futbol/tribuna',
    shareText: 'Subite a la tribuna de Pacto Mundial 🔥 https://pactomundial.futbol/tribuna',
});

export const buildShareTribunaButtonHTML = () => `
    <button id="tribunaShareBtn" type="button" aria-label="Compartir tribuna pública"
        style="position:absolute; top:0; right:0; z-index:2; width:34px; height:34px; min-height:34px; border-radius:50%;
               border:1px solid rgba(255,255,255,.16); background:rgba(255,255,255,.06); color:var(--white);
               cursor:pointer; padding:0; margin:0; display:flex; align-items:center; justify-content:center;
               font-size:1rem; line-height:1; transition:transform .15s ease, opacity .15s ease;"
        onmouseover="this.style.opacity='.85';this.style.transform='scale(1.05)'"
        onmouseout="this.style.opacity='1';this.style.transform='scale(1)'">🔗</button>
`;

// Errores crudos de los RPCs de tribuna → mensaje de UX.
const traducirErrorTribuna = (error) => {
    const m = (error?.message || '').toLowerCase();
    if (m.includes('no_sos_este_jugador') || m.includes('no_sos_miembro')) return 'Para reaccionar entrá con tu grupo.';
    if (m.includes('ritual_inexistente')) return 'Ese ritual ya no está disponible.';
    if (m.includes('no_autenticado')) return 'Tu sesión expiró. Recargá la página.';
    return 'No pudimos registrar tu reacción. Probá de nuevo.';
};

// Etapa 3 — handle del timer de refresco periódico de la muralla. A nivel
// módulo (un solo timer vivo): se limpia al re-entrar a renderTribuna y el
// propio tick se auto-cancela si el usuario salió de la tribuna.
let _rotacionTimer = null;

// DT-H3 (5-jun-2026): pantalla de fallback cuando el feed de la tribuna no carga
// (timeout/red). Reemplaza el spinner infinito por "Reintentar". El botón
// data-tribuna-retry lo cablea cada vista a su propio re-render.
const buildTribunaErrorHTML = (codigo, currentUser, titulo = 'No se pudo cargar la tribuna') => `
    <div class="view-enter" style="width:100%;">
        <div class="glass-card" style="text-align:center; margin-top:2rem;">
            <h2 style="font-family:var(--font-heading); color:var(--white); font-size:1.2rem; margin-bottom:.5rem;">${titulo}</h2>
            <p class="subtitle" style="margin-bottom:1rem;">Revisá tu conexión y probá de nuevo.</p>
            <button class="btn btn-primary" type="button" data-tribuna-retry style="min-height:46px;">Reintentar</button>
        </div>
        ${currentUser ? renderBottomNav(codigo, 'tribuna') : ''}
    </div>`;

export const renderTribuna = async (codigo, opts = {}) => {
    // FASE 3.17 · Bloque D — opts.destacarArenga viene de renderTribunaPublica
    // cuando el usuario entra por /t/AUDIO_ID. Reutilizamos sharedArengaId
    // para que el modal de detalle se abra automáticamente.
    const sharedArengaId = new URLSearchParams(window.location.hash.split('?')[1] || '').get('arenga')
        || opts.destacarArenga?.arenga_id
        || null;
    const currentUser = getCurrentUser();
    const canInteractPublic = Boolean(currentUser);
    const jugadorActivo = getMembership(codigo);

    appRoot.innerHTML = buildTribunaSkeleton();
    // SIEMPRE que se pinta el esqueleto (= navegación a la Tribuna, no realtime):
    // tapar el esqueleto con la foto. El realtime hace updates in-place (no pinta
    // esqueleto ni llama renderTribuna), así que no flashea durante el uso.
    showLoadingOverlay();

    // F1.4.b: limpiar suscripciones realtime previas y montar la nueva al final
    // del render (después de pintar). El re-render lo dispara cualquier INSERT
    // en arengas / reacciones_ritual / cabalas / promesas (la tribuna agrega
    // los 3 tipos), invalidando la caché del feed.
    limpiarSuscripciones('tribuna');
    // Etapa 3: matar el timer de rotación previo antes de montar el nuevo.
    if (_rotacionTimer) { clearInterval(_rotacionTimer); _rotacionTimer = null; }

    const tribunaArengaTypes = new Proxy({ ...ARENGA_TYPES_BASE }, { get: (t, k) => t[k] || t.vamos });
    // perf-tribuna (30-may): el primer pintado trae solo ~60 arengas (las que se
    // ven) en vez de las 500; las 500 entran de fondo post-paint (más abajo) para
    // alimentar la rotación de la etapa 3. La muralla aparece rápido y rota sobre
    // los 60 sin cortarse mientras llegan las 500.
    // El contador "N arengas creadas" del banner sale ahora del RPC
    // contar_arengas_total (universo amplio, sin filtro) — NUNCA de la longitud
    // del feed. Va en la misma tanda paralela (cero waterfall).
    // Cap de arengas para anónimo (CONSTRUIDO APAGADO): el flag solo importa sin
    // login; logueado nunca tiene tope, así que no gasta el RPC. Degrada a OFF.
    // DT-H3: el feed tiene timeout (core/tribuna.js); si falla/timeoutea PROPAGA
    // → mostramos "Reintentar" en vez de spinner eterno. Los contadores degradan
    // a 0 solos (no tiran). El skeleton ya pintado se reemplaza por el error.
    let feed, guardadasRaw, cntArengas, cntCabalas, cntPromesas, cntReacciones, capArengasFlag;
    try {
        [feed, guardadasRaw, cntArengas, cntCabalas, cntPromesas, cntReacciones, capArengasFlag] = await Promise.all([
            getFeedTribuna('arenga', { limit: 12 * TRIBUNA_LANE_CARDS }),
            currentUser ? getArengasGuardadasDelUsuario(currentUser.user_id) : Promise.resolve([]),
            contarArengasTotal(),
            contarCabalasActivas(),
            contarPromesasActivas(),
            contarReaccionesTotal(),
            currentUser ? Promise.resolve({ enabled: false }) : getCapArengasAnon(),
        ]);
    } catch (err) {
        console.error('[tribuna] carga del feed falló:', err?.message || err);
        hideLoadingOverlay(); // que se vea el "Reintentar", no la foto
        if (!esRutaConRenderTribuna(window.location.hash)) return;
        appRoot.innerHTML = buildTribunaErrorHTML(codigo, currentUser);
        appRoot.querySelector('[data-tribuna-retry]')?.addEventListener('click', () => renderTribuna(codigo, opts));
        return;
    }
    const savedSet = new Set(guardadasRaw.map(g => g.arenga_id));
    // Spec arengas v1.2 §9/§10: marca visual de la reacción del jugador a cada
    // arenga del feed (botón activo) + set de al_pasto ya reportados. Solo
    // logueado y miembro de algún grupo. Las 2 queries son independientes →
    // paralelas (antes secuenciales). `let` porque el background (500) las
    // recomputa sobre la ventana completa y reasigna (cobertura idéntica a hoy).
    const feedArengaIds = (feed || []).map(a => a.arenga_id);
    let [miReaccionMap, miAlPastoSet] = (jugadorActivo?.id && feedArengaIds.length)
        ? await Promise.all([
            getMisReaccionesArengas(jugadorActivo.id, feedArengaIds),
            getMisAlPastoArengas(jugadorActivo.id, feedArengaIds),
        ])
        : [{}, new Set()];
    // FASE 3.17 · Bloque D fix: el link viral entra por /t/AUDIO_ID (no por
    // /tribuna/...). Ampliamos la guarda anti race-de-navegación para no
    // abortar el render cuando el usuario llega via el shortcut viral.
    if (!esRutaConRenderTribuna(window.location.hash)) return;

    const reactionTotal = (a, reaction) => reaction === 'pasto'
        ? (a.pasto || 0)
        : (a[`${reaction}Grupo`] || 0) + (a[`${reaction}Publico`] || 0) + (a[reaction] || 0);
    const reactionDefs = [
        { id: 'escuchar', label: 'Escuchen esto', icon: 'M6 12h7l5-7v14l-5-7H6ZM4 9v6', color: '#75aadb' },
        { id: 'pego', label: 'Vamo arriba !', icon: 'M12 3c3 3 4 5.5 2 8 2-.5 3-2 3-4 2 2 3 4.4 3 7a8 8 0 1 1-16 0c0-3 1.4-5.4 4-7-.2 2.4.7 4 2.5 5C9.8 8.5 10.5 5.5 12 3Z', color: '#fb923c' },
        { id: 'loco', label: 'Jaja !', icon: 'M7 10h10M8 14c2.4 2.2 5.6 2.2 8 0M8.5 8h.1M15.5 8h.1M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', color: '#facc15' },
        { id: 'pasto', label: 'Te fuiste al pasto', icon: 'M5 18h9c2.4 0 4-1.6 4-4v-1h-5l-2-6H8l1.8 6H5v5Z', color: '#c084fc' }
    ];
    const isArengaSaved = (id) => savedSet.has(id);
    const renderVisibilityBadge = (visibilidad) => {
        const isPublic = visibilidad === 'publica';
        const icon = isPublic ? 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.6 9h16.8M3.6 15h16.8M12 3c2.1 2.4 3.2 5.4 3.2 9s-1.1 6.6-3.2 9c-2.1-2.4-3.2-5.4-3.2-9S9.9 5.4 12 3Z' : 'M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6v-9Z';
        return `<span style="display:inline-flex;align-items:center;gap:.32rem;border-radius:999px;padding:.22rem .48rem;border:1px solid ${isPublic ? 'rgba(34,197,94,.24)' : 'rgba(117,170,219,.24)'};background:${isPublic ? 'rgba(34,197,94,.08)' : 'rgba(117,170,219,.08)'};color:${isPublic ? '#86efac' : 'var(--celeste)'};font-size:.64rem;font-weight:900;">${renderPactoIcon(icon, isPublic ? '#86efac' : '#75aadb', 12)} ${isPublic ? 'Pública' : 'Privada'}</span>`;
    };

    const renderPublicArengaTypeBadge = (type, size = 'small') => {
        const meta = tribunaArengaTypes[type] || tribunaArengaTypes.vamos;
        const dimension = size === 'large' ? '68px' : size === 'medium' ? '46px' : '34px';
        const iconSize = size === 'large' ? 34 : size === 'medium' ? 24 : 18;
        return `<span title="${meta.label}" style="width: ${dimension}; height: ${dimension}; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(135deg, ${meta.color} 0%, rgba(255,255,255,0.14) 100%); border: 1px solid rgba(255,255,255,0.18); box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 18px ${meta.color}28;">${renderPactoIcon(meta.icon, '#ffffff', iconSize)}</span>`;
    };

    // Feed público de arengas (tribuna_feed, ordenado por score). Adaptado al
    // shape legacy del HTML-gen — los contadores de reacción salen del RPC.
    // Etapa 3: extraído a mapFeedArenga para que el tick periódico re-mapee el
    // feed fresco con el MISMO shape (rotación + score actualizado in-place).
    const mapFeedArenga = (a) => {
        const segundos = a.duracion_segundos || 0;
        const reac = a.reacciones || {};
        return {
            id: a.arenga_id,
            audioId: a.audio_id,
            audioUrl: a.audio_url && a.audio_url !== 'pendiente' ? a.audio_url : '',
            tipo: a.tipo_arenga,
            titulo: a.titulo || '',
            duracion: `0:${String(segundos).padStart(2, '0')}`,
            segundos,
            creadorId: a.creador_id,
            creador: a.creador_nombre || 'Jugador',
            grupoOrigen: a.grupo_nombre || 'Tribuna',
            visibilidad: 'publica',
            createdAt: new Date(a.created_at).getTime(),
            escuchar: reac.escuchar || 0,
            pego: reac.pego || 0,
            loco: reac.loco || 0,
            pasto: reac.al_pasto || 0,
            miReaccion: miReaccionMap[a.arenga_id] || null,
            miAlPasto: miAlPastoSet.has(a.arenga_id),
        };
    };
    const publicArengas = feed.map(mapFeedArenga);

    // FASE 3.17 · Bloque D — si la arenga destacada (link viral) no está en
    // el feed (caso edge: feed limit 60, arenga vieja), la prependemos al
    // array para que el modal de detalle la encuentre y se abra.
    if (opts.destacarArenga && !publicArengas.find((a) => a.id === opts.destacarArenga.arenga_id)) {
        const d = opts.destacarArenga;
        const dSeg = d.duracion_segundos || 0;
        publicArengas.unshift({
            id: d.arenga_id,
            audioId: d.audio_id,
            audioUrl: d.audio_url && d.audio_url !== 'pendiente' ? d.audio_url : '',
            tipo: d.tipo_arenga,
            titulo: d.titulo || '',
            duracion: `0:${String(dSeg).padStart(2, '0')}`,
            segundos: dSeg,
            creadorId: d.creador_id,
            creador: d.jugadores?.display_name || 'Jugador',
            grupoOrigen: d.grupos?.name || 'Tribuna',
            visibilidad: d.es_publica ? 'publica' : 'grupo',
            createdAt: new Date(d.created_at).getTime(),
            escuchar: 0, pego: 0, loco: 0, pasto: 0,
            miReaccion: miReaccionMap[d.arenga_id] || null,
            miAlPasto: miAlPastoSet.has(d.arenga_id),
        });
    }
    const selectedArenga = publicArengas.find(a => a.id === sharedArengaId);
    // perf-tribuna vidriera (31-may): estado MUTABLE de la muralla.
    //   rankedActual        = arengas DISTINTAS ordenadas por C (pool del que la
    //                         vidriera elige; el background lo amplía a 500).
    //   publicArengasActual = alias para el `find` del overlay (listener delegado).
    //   seenBombo           = memoria del bombo entre renovaciones (vuelta completa
    //                         → reinicia). Vive en este closure de renderTribuna.
    //   filasActuales       = 12 arrays de cardsPorLane items, ya barajados
    //                         (destacados entremezclados con bombo, sin zona VIP).
    let rankedActual = publicArengas;
    let publicArengasActual = publicArengas;
    let seenBombo = new Set();
    // cardsPorLane FIJO = TRIBUNA_LANE_CARDS (8). El strip pinta siempre 8×3 →
    // ancho constante entre renovaciones → el loop -33.333% sigue seamless. Con
    // pocas reales se rellena ciclando (rellenarHastaN) para igual pintar 8/fila.
    const cardsPorLane = TRIBUNA_LANE_CARDS;
    const TOTAL_VISIBLE = 12 * cardsPorLane; // 96
    // construirFilas: corre la vidriera (bombo + destacados) sobre rankedActual,
    // baraja el conjunto COMPLETO (destacados entremezclados con bombo) y lo parte
    // en 12 filas de cardsPorLane. Actualiza seenBombo (memoria del bombo).
    const construirFilas = () => {
        const sel = seleccionarVidriera(rankedActual, seenBombo, {
            destacadosN: TRIBUNA_DESTACADOS, totalN: TOTAL_VISIBLE,
        });
        seenBombo = sel.seen;
        // Barajar TODO antes de repartir → cada fila mezcla destacados y bombo
        // (sin "zona VIP" arriba). Rellenar a 96 si hay pocas reales (cicla).
        const pool = rellenarHastaN(barajar(sel.seleccion), TOTAL_VISIBLE);
        const filas = [];
        for (let i = 0; i < 12; i++) filas.push(pool.slice(i * cardsPorLane, (i + 1) * cardsPorLane));
        return filas;
    };
    let filasActuales = construirFilas();
    const totalReactions = (a) => ['escuchar', 'pego', 'loco', 'pasto'].reduce((total, reaction) => total + reactionTotal(a, reaction), 0);
    // Spec arengas v1.2 §5/§8: tiempo "hace XX" obligatorio en card cerrada
    // y despliegue, en grupo y Tribuna. Helper compartido extraido a
    // src/utils/time-ago.js (antes era un closure triplicado).
    const arengaCreatedAgo = (createdAt) => formatTimeAgo(createdAt);
    // F1.4.b: contadores reales (resuelven DT-BX/DT-BY).
    // total_movidas_tribuna = arengas + cábalas + promesas + reacciones (toda la
    // actividad tribunera real de la comunidad). Reemplaza al viejo
    // total_jugadores_activos en la pastilla verde — dato más grande y honesto.
    // perf-tribuna (30-may): el contador de arengas sale del RPC contar_arengas_total
    // (universo amplio real), NUNCA de publicArengas.length (que es solo lo que
    // trajo el feed = 60). Idéntico criterio "números grandes" que los otros 3.
    const metrics = {
        total_arengas_activas: cntArengas,
        total_cabalas_activadas: cntCabalas,
        total_promesas_activas: cntPromesas,
        total_movidas_tribuna: cntArengas + cntCabalas + cntPromesas + cntReacciones,
    };
    const formatMetric = (value) => value.toLocaleString('es-AR');
    // renderMiniAudio: usa el componente compartido src/ui/arenga-card.js
    // (extraído 28-may-2026 para reutilizar en perfil "Mis arengas guardadas").
    const renderMiniAudio = (a, _index) => renderArengaCard({
        arenga: {
            id: a.id,
            tipo: a.tipo,
            duracion: a.duracion,
            grupoOrigen: a.grupoOrigen || a.provincia,
            reacCount: totalReactions(a),
            pts: puntosPorRitual({ reaccionesTotal: totalReactions(a) }),
            ago: arengaCreatedAgo(a.createdAt),
        },
        mode: 'wall',
        className: 'tribuna-audio-open',
        // Sin nav por hash: el listener delegado abre el overlay (etapa 4) → la
        // muralla NO se re-renderiza. data-id (de renderArengaCard) identifica la arenga.
        clickHandler: '',
    });
    const institutionalBanners = [
        { title: 'EL MUNDIAL M&Aacute;S PASIONAL DEL PLANETA', subtext: 'Folklore y pasi&oacute;n futbolera', cta: '&#9889; QUIERO JUGAR &rarr;' },
        { title: 'EN ARGENTINA LO VIVIMOS AS&Iacute;', subtext: '', cta: '&#9889; SUMATE &rarr;' },
        { title: 'LA TRIBUNA ES CON AMIGOS', subtext: 'Ranking y rituales futboleros argentinos', cta: '&#9889; QUIERO ENTRAR &rarr;' }
    ];
    const renderInstitutionalBanner = (banner) => `
        <span class="tribuna-hero-wrap"><button class="tribuna-join-open tribuna-hero-led tribuna-hero-marquee" type="button" style="width:100%;height:108px;border:1px solid rgba(245,158,11,0.48);border-radius:15px;background:radial-gradient(circle at 10% 0%, rgba(245,158,11,0.34), transparent 42%), radial-gradient(circle at 96% 18%, rgba(117,170,219,0.28), transparent 36%), linear-gradient(135deg, rgba(15,23,42,0.98), rgba(12,18,30,0.94));color:var(--white);padding:0.72rem 1rem;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.5rem;text-align:center;cursor:pointer;box-shadow:inset 0 1px 0 rgba(255,255,255,0.14);position:relative;z-index:1;overflow:hidden;">
            <span style="display:flex;flex-direction:column;align-items:center;min-width:0;width:100%;gap:0.44rem;">
                <strong style="display:block;font-family:var(--font-heading);font-size:1.03rem;line-height:1.05;text-transform:uppercase;color:var(--white);text-shadow:0 0 14px rgba(255,255,255,0.16);white-space:normal;overflow-wrap:anywhere;text-align:center;">${banner.title}</strong>
                <span style="align-self:center;color:#06111f;background:linear-gradient(135deg,#fde68a,#f59e0b);border:1px solid rgba(255,255,255,0.28);border-radius:999px;padding:0.46rem 0.94rem;font-weight:950;font-size:0.78rem;line-height:1;white-space:nowrap;box-shadow:0 0 20px rgba(245,158,11,0.42);letter-spacing:0;text-align:center;">${banner.cta}</span>
                ${banner.subtext ? `<em style="display:block;color:rgba(255,255,255,0.66);font-style:normal;font-size:0.67rem;line-height:1.12;white-space:normal;text-align:center;">${banner.subtext}</em>` : ''}
            </span>
        </button></span>
    `;
    const renderInstitutionalLane = () => {
        const banners = institutionalBanners.map(renderInstitutionalBanner).join('');
        return `
            <div class="tribuna-ad-row" aria-label="Mensajes institucionales de PACTO MUNDIAL">
                <div class="tribuna-ad-strip">
                    ${[banners, banners, banners, banners].join('')}
                </div>
            </div>
        `;
    };
    // Contenido (triplicado ×3) de UN carril = su fila de la vidriera (8 cards
    // distintas, ya barajadas). Lo usan el render inicial (renderAudioWallLane) y
    // la renovación (strip.innerHTML = buildLaneInner(i)) → el nodo strip persiste,
    // no remonta. ×3 intacto: el loop -33.333% sigue seamless.
    const buildLaneInner = (laneIndex) => {
        const laneItems = (filasActuales[laneIndex] || [])
            .map((a, index) => renderMiniAudio(a, index + laneIndex));
        return [...laneItems, ...laneItems, ...laneItems].join('');
    };
    const renderAudioWallLane = (laneIndex) => {
        // perf-tribuna mobile (31-may): rango de velocidad APLANADO y CALMO para
        // aliviar al celu. Con 6 cards/fila la copia mide 6*150 + 5*6.08 = 930px
        // (strip x3 = 2.803px < 4096). El loop mueve 1 copia (-33.333%) por
        // `duration`, asi que velocidad px/s = 930 / duration. Formula nueva:
        // duration = 16 + (laneIndex%6)*1.6 -> 16..24s -> ~58..39 px/s. La fila
        // mas rapida (58 px/s) queda notablemente mas tranquila que los ~84 px/s
        // que corrian las filas rapidas antes; NINGUNA supera ese piso de 84.
        // ×3, keyframes y el loop seamless no se tocan.
        const duration = 16 + ((laneIndex % 6) * 1.6);
        const reverse = laneIndex % 3 === 1;
        const offset = (laneIndex % 4) * -42;
        const rowHeight = '39px';
        return `
            <div class="tribuna-wall-row" style="--duration:${duration}s; --offset:${offset}px; --row-height:${rowHeight};">
                <div class="tribuna-wall-strip ${reverse ? 'reverse' : ''}">
                    ${buildLaneInner(laneIndex)}
                </div>
            </div>
        `;
    };
    // Overlay de detalle de arenga (etapa 4): se monta/desmonta en document.body
    // SIN tocar la URL (no re-renderiza la muralla). `selectedArenga` queda como
    // alias local del parámetro para reutilizar el cuerpo verbatim. El cierre
    // (X / tap-afuera / ESC) se cablea en JS (wireArengaOverlay), no por hash.
    const buildArengaOverlayHTML = (arenga) => {
        const selectedArenga = arenga;
        const meta = tribunaArengaTypes[selectedArenga.tipo] || tribunaArengaTypes.vamos;
        return `
            <div data-arenga-detail-open="1" style="position: fixed; inset: 0; z-index: 80; background: rgba(0,0,0,0.50); display: flex; align-items: flex-start; justify-content: center; padding: 10.2rem 0.72rem 0.72rem; padding: clamp(10.2rem, 30vh, 13rem) 0.72rem 0.72rem; overflow-y: auto;">
                <div class="glass-card" style="width: min(100%, 420px); max-height: calc(100vh - 10.95rem); max-height: calc(100dvh - clamp(10.95rem, 31vh, 13.75rem)); overflow-y: auto; padding: 0.72rem; border-color: ${meta.color}55; background: radial-gradient(circle at 18% 0%, ${meta.color}24, transparent 34%), rgba(12,18,30,0.96); border-radius: 14px;">
                    <div style="display: flex; justify-content: space-between; gap: 0.65rem; align-items: flex-start; margin-bottom: 0.52rem;">
                        <div style="display: flex; gap: 0.58rem; align-items: center; min-width: 0;">
                            ${renderPublicArengaTypeBadge(selectedArenga.tipo, 'medium')}
                            <div style="min-width: 0;">
                                <span style="display: block; color: ${meta.color}; font-weight: 900; font-size: 0.66rem; text-transform: uppercase;">${meta.label}</span>
                                <h3 style="margin: 0.12rem 0 0; color: var(--white); font-family: var(--font-heading); font-size: 0.98rem; line-height: 1.02; overflow-wrap: anywhere;">${selectedArenga.titulo || meta.label}</h3>
                            </div>
                        </div>
                        <button class="btn btn-secondary" data-arenga-close style="width: auto; min-height: 30px; margin: 0; padding: 0.28rem 0.52rem; border-radius: 999px;">X</button>
                    </div>

                    <div style="padding: 0.56rem; border-radius: 11px; background: rgba(255,255,255,0.052); border: 1px solid rgba(255,255,255,0.09);">
                        ${selectedArenga.audioUrl ? `<audio id="tribunaAudio" preload="metadata" src="${selectedArenga.audioUrl}" style="display:none;"></audio>` : ''}
                        <div style="display:flex;align-items:center;gap:.6rem;">
                            <button id="tribunaAudioPlay" class="btn btn-primary" ${selectedArenga.audioUrl ? '' : 'disabled'} style="width: 42px; height: 42px; min-height: 42px; border-radius: 50%; margin: 0; padding: 0; display: flex; align-items: center; justify-content: center; font-size: 0.76rem; background: linear-gradient(135deg, ${meta.color} 0%, var(--celeste) 100%); border: none; ${selectedArenga.audioUrl ? '' : 'opacity:.45;'}">Play</button>
                            <div style="flex:1;min-width:0;">
                                <div style="height: 6px; border-radius: 999px; overflow: hidden; background: rgba(255,255,255,0.12);">
                                    <div id="tribunaAudioProgress" style="width: 0%; height: 100%; background: linear-gradient(90deg, ${meta.color}, var(--celeste)); transition: width 0.25s linear;"></div>
                                </div>
                                <div style="display: flex; justify-content: space-between; color: var(--text-muted); font-size: 0.64rem; margin-top: 0.32rem;">
                                    <span>0:00</span>
                                    <span>${selectedArenga.duracion}</span>
                                </div>
                            </div>
                        </div>
                        ${selectedArenga.audioUrl ? '' : '<span style="display:block;color:var(--text-muted);font-size:.66rem;margin-top:.34rem;">Audio aún no subido.</span>'}
                    </div>

                    <div style="display: flex; flex-wrap: wrap; gap: 0.34rem; margin-top: 0.48rem; align-items: center;">
                        <span style="font-size: 0.7rem; color: var(--text-muted);">De ${selectedArenga.creador}</span>
                        <span style="font-size: 0.7rem; color: var(--text-muted);">${selectedArenga.grupoOrigen}</span>
                        ${renderVisibilityBadge(selectedArenga.visibilidad)}
                    </div>
                    <!-- Spec arengas v1.2 §8: tiempo obligatorio en despliegue.
                         En linea propia debajo del creador+grupo para que no quede mezclado. -->
                    <div style="margin-top:.32rem;display:flex;justify-content:space-between;gap:.5rem;font-size:.7rem;">
                        <span style="color:var(--text-muted);">${arengaCreatedAgo(selectedArenga.createdAt)}</span>
                        ${(() => { const t = totalReactions(selectedArenga); const p = puntosPorRitual({ reaccionesTotal: t }); return `<span data-rit-pts="arenga:${selectedArenga.id}" data-rit-reac="${t}" style="color:#7ab488;">Puntos: ${p>0?'+':''}${p}</span>`; })()}
                    </div>

                    ${(jugadorActivo?.id && selectedArenga.creadorId && jugadorActivo.id.toString() === selectedArenga.creadorId.toString()) ? `
                        <div style="margin-top:.58rem;padding:.36rem .5rem;border-radius:11px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.66rem;text-align:center;">Es tu arenga — no podés reaccionar a vos mismo.</div>
                        <!-- Decisión 28-may del dueño: el creador ve los 4 contadores
                             reales (3 sociales + Al pasto), apagados. Mismo patrón
                             que renderArengaDetail en src/modules/rituales.js. -->
                        <div style="display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.3rem; margin-top: 0.4rem;">
                            ${reactionDefs.map(reaction => `
                                <div aria-disabled="true" style="margin:0; min-height:36px; padding:0.26rem 0.2rem; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:10px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.1rem; opacity:.55; cursor:default; user-select:none;">
                                    <span style="display:flex;align-items:center;gap:.12rem;color:var(--white);font-size:0.58rem;line-height:1;white-space:nowrap;">${renderPactoIcon(reaction.icon, reaction.color, 11)} ${reaction.label}</span>
                                    <span style="color:${reaction.color};font-size:0.64rem;font-weight:900;">${reactionTotal(selectedArenga, reaction.id)}</span>
                                </div>
                            `).join('')}
                        </div>
                    ` : `
                        <div style="display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.3rem; margin-top: 0.58rem;">
                            ${reactionDefs.filter(reaction => reaction.id !== 'pasto').map(reaction => {
                                const active = selectedArenga.miReaccion === reaction.id;
                                const bg = active ? `${reaction.color}29` : (reaction.id === 'escuchar' ? 'rgba(117,170,219,0.12)' : 'rgba(255,255,255,0.055)');
                                const border = active ? `${reaction.color}cc` : (reaction.id === 'escuchar' ? 'rgba(117,170,219,0.3)' : 'rgba(255,255,255,0.1)');
                                return `
                                    <button class="btn btn-secondary tribuna-audio-react${active ? ' tribuna-audio-react-active' : ''}" data-id="${selectedArenga.id}" data-reaction="${reaction.id}" data-active="${active ? '1' : '0'}" style="margin:0; min-height:36px; padding:0.26rem 0.2rem; background:${bg}; border-color:${border}; ${active ? 'box-shadow: 0 0 0 1px ' + reaction.color + '55 inset;' : ''} display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.1rem;">
                                        <span style="display:flex;align-items:center;gap:.12rem;color:var(--white);font-size:0.58rem;line-height:1;white-space:nowrap;">${renderPactoIcon(reaction.icon, reaction.color, 11)} ${reaction.label}</span>
                                        <span style="color:${reaction.color};font-size:0.64rem;font-weight:900;">${reactionTotal(selectedArenga, reaction.id)}</span>
                                    </button>
                                `;
                            }).join('')}
                        </div>
                    `}

                    <div style="display:grid;grid-template-columns:${(jugadorActivo?.id && selectedArenga.creadorId && jugadorActivo.id.toString() === selectedArenga.creadorId.toString()) ? '1fr' : '3fr 1fr'};gap:.38rem;margin-top:.42rem;">
                        <button class="btn btn-secondary tribuna-audio-save" data-id="${selectedArenga.id}" data-saved="${isArengaSaved(selectedArenga.id) ? '1' : '0'}" style="margin:0; min-height:34px; padding:0.4rem 0.55rem; background:${isArengaSaved(selectedArenga.id) ? 'rgba(34,197,94,.14)' : 'rgba(255,255,255,0.055)'}; border-color:${isArengaSaved(selectedArenga.id) ? 'rgba(34,197,94,.32)' : 'rgba(255,255,255,0.12)'}; color:${isArengaSaved(selectedArenga.id) ? '#bbf7d0' : 'var(--white)'}; font-size:0.7rem; font-weight:${isArengaSaved(selectedArenga.id) ? '900' : '600'};">
                            ${isArengaSaved(selectedArenga.id) ? '✓ Guardada' : 'Guardar arenga'}
                        </button>
                        ${(jugadorActivo?.id && selectedArenga.creadorId && jugadorActivo.id.toString() === selectedArenga.creadorId.toString()) ? '' : `<button class="btn btn-secondary tribuna-audio-react${selectedArenga.miAlPasto ? ' tribuna-audio-react-active' : ''}" data-id="${selectedArenga.id}" data-reaction="pasto" data-active="${selectedArenga.miAlPasto ? '1' : '0'}" style="margin:0; min-height:34px; padding:0.38rem 0.22rem; background:rgba(192,132,252,${selectedArenga.miAlPasto ? '0.22' : '0.08'}); border-color:rgba(192,132,252,${selectedArenga.miAlPasto ? '0.6' : '0.18'}); color:#ddd6fe; font-size:0.62rem; display:flex; align-items:center; justify-content:center; gap:.28rem; ${selectedArenga.miAlPasto ? 'box-shadow:0 0 0 1px rgba(192,132,252,.35) inset;' : ''}"><span>${selectedArenga.miAlPasto ? '✓ Reportado' : 'Al pasto'}</span><span style="color:#c084fc;font-weight:900;">${reactionTotal(selectedArenga, 'pasto')}</span></button>`}
                    </div>

                    ${selectedArenga.audioId ? `
                        <button class="btn btn-secondary tribuna-audio-share" data-id="${selectedArenga.id}" data-audio-id="${selectedArenga.audioId}" data-titulo="${(selectedArenga.titulo || 'arenga mundialista').replace(/"/g, '&quot;')}" data-creador="${selectedArenga.creador.replace(/"/g, '&quot;')}" style="width:100%;margin:.42rem 0 0;min-height:38px;padding:.48rem .62rem;border-radius:999px;background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.18);color:#bbf7d0;font-size:.74rem;font-weight:900;">🔗 Compartir</button>
                    ` : ''}
                </div>
            </div>
        `;
    };

    appRoot.innerHTML = `
        <!-- Override LOCAL (solo Tribuna Pública / header SVG): arranca al tope del
             viewport. #app-root tiene padding-top:1.375rem GLOBAL; acá lo cancelamos
             ENTERO con margin-top negativo y re-sumamos el safe-area-inset-top (notch).
             El valor (-1.375rem) está acoplado al padding-top de #app-root: si cambia,
             actualizar acá. Sin viewport-fit=cover el inset es 0 hoy; la fórmula queda
             robusta si se agrega. -->
        <div class="view-enter" style="width: 100%; margin-top: calc(env(safe-area-inset-top, 0px) - 1.375rem); animation: fadeIn 0.4s ease-out forwards;">
            <style>
                @keyframes tribunaFloatLeft { from { transform: translate3d(var(--offset),0,0); } to { transform: translate3d(calc(-33.333% + var(--offset)),0,0); } }
                @keyframes tribunaFloatRight { from { transform: translate3d(calc(-33.333% + var(--offset)),0,0); } to { transform: translate3d(var(--offset),0,0); } }
                @keyframes tribunaPulseDot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .45; transform: scale(.72); } }
                @keyframes tribunaHeroGlowPulse { 0%, 100% { opacity: 0.58; } 50% { opacity: 1; } }
                @keyframes tribunaHeroSweep { 0% { transform: translateX(-120%) skewX(-16deg); opacity: 0; } 22% { opacity: .55; } 42% { transform: translateX(130%) skewX(-16deg); opacity: 0; } 100% { transform: translateX(130%) skewX(-16deg); opacity: 0; } }
                @keyframes tribunaAdMarquee { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }
                .tribuna-wall { position: relative; overflow: hidden; contain: paint; margin: 0 -1rem 1rem; padding: .05rem 0 .35rem; }
                /* perf-tribuna pico ~2s (30-may): el fade lateral pasó de mask-image
                   (recomposición por-frame al moverse el contenido bajo el mask, cara
                   en mobile) a dos overlays estáticos de borde. Mismo fade del 8%, al
                   color real del fondo (--bg-dark #090e17). pointer-events:none → no
                   roban clicks; z-index 3 > cards (z-index 2) → desvanecen igual que
                   el mask. Son capas estáticas: no se recomponen con el marquee. */
                .tribuna-wall::before, .tribuna-wall::after { content: ''; position: absolute; top: 0; bottom: 0; width: 8%; z-index: 3; pointer-events: none; }
                .tribuna-wall::before { left: 0; background: linear-gradient(90deg, var(--bg-dark) 0%, transparent 100%); }
                .tribuna-wall::after { right: 0; background: linear-gradient(270deg, var(--bg-dark) 0%, transparent 100%); }
                .tribuna-wall-row { height: var(--row-height, 39px); overflow: hidden; margin-bottom: 3px; }
                /* perf-tribuna pico ~2s (31-may): el strip vive en una capa propia
                   ESTABLE. Con el strip ya <4096px (una sola textura, desde 094b6c6),
                   will-change:transform sobre el strip — y SOLO el strip — fuerza al
                   compositor a rasterizarlo UNA vez y de ahí solo componer (mover) sin
                   re-dibujar cuando entra una card por el borde. backface-visibility
                   fija la capa. NO se re-pone will-change en .tribuna-hero-led ni en
                   varios nodos (eso causaba el temblor constante que mató e8b27cb): acá
                   es un nodo por fila, el único que se anima. Animación 100% compositor
                   (transform puro), velocidad idéntica (--duration intacto). */
                .tribuna-wall-strip { display: flex; gap: .38rem; width: max-content; animation: tribunaFloatLeft var(--duration) linear infinite; transform: translateZ(0); will-change: transform; backface-visibility: hidden; -webkit-backface-visibility: hidden; }
                .tribuna-wall-strip.reverse { animation-name: tribunaFloatRight; }
                .tribuna-audio-open { pointer-events: auto; }
                .tribuna-ad-row { height: 114px; overflow: hidden; margin: .28rem 0 .34rem; }
                .tribuna-ad-strip { display: flex; gap: .75rem; width: max-content; will-change: transform; animation: tribunaAdMarquee 92s linear infinite; transform: translateZ(0); }
                /* perf-tribuna glow (31-may, aprobado): el latido institucional dejó de
                   animar box-shadow (repaint por frame en los banners del marquee) → es
                   OPACITY-ONLY. El glow (peak) vive ESTÁTICO en .tribuna-hero-wrap::before,
                   una capa DETRÁS del banner (fuera del overflow:hidden → no se clipea), y
                   solo se le anima la opacity (0.58↔1 = mismas alphas que el viejo
                   trough↔peak). Mismo ritmo 3.2s, mismo aspecto, 100% compositor. El banner
                   conserva fondo/borde/inset; el sweep ::after (.tribuna-hero-led) sigue igual. */
                .tribuna-hero-wrap { position: relative; display: block; flex: 0 0 min(88vw, 390px); height: 108px; border-radius: 15px; }
                .tribuna-hero-wrap::before { content: ''; position: absolute; inset: 0; border-radius: 15px; box-shadow: 0 0 44px rgba(245,158,11,0.38), 0 0 28px rgba(117,170,219,0.2); animation: tribunaHeroGlowPulse 3.2s ease-in-out infinite; pointer-events: none; z-index: 0; }
                .tribuna-hero-led::after { content: ''; position: absolute; inset: -20% auto -20% 0; width: 42%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent); animation: tribunaHeroSweep 5.6s ease-in-out infinite; pointer-events: none; }
                /* feat-tribuna encabezado (01-jun): la franja-bandera animada se
                   reemplazo por dos banderas SVG estaticas que flanquean el titulo
                   (mismo asset que la pantalla de inicio). CSS .tribuna-flag* y el
                   keyframe tribunaFlagWave eliminados — markup y reglas ya no existen. */
            </style>

            <!-- feat-tribuna encabezado SVG (04-jun): banderas + título "Tribuna
                 Pública / Argentina" + subtítulo "Pacto Mundial" reemplazados por
                 el SVG provisto por el dueño. Contenedor full-bleed con el MISMO
                 margin lateral que la barra ARENGAS de abajo (margin 0 -1rem) →
                 bordes izquierdo/derecho idénticos a ARENGAS. Pegado a esa barra
                 (margin-bottom 0). El <img> escala SIEMPRE proporcional (width:100%
                 del contenedor + height:auto, sin height fijo). El botón de
                 compartir queda absoluto arriba-derecha RELATIVO al header, mismo
                 handler. -->
            <div style="position: relative; margin: 0.08rem -1rem 0;">
                <picture>
                    <source srcset="${tribunaHeaderWebp}" type="image/webp">
                    <img src="${tribunaHeaderPng}" alt="Tribuna Pública Argentina" style="display:block; width:100%; height:auto;">
                </picture>
                ${buildShareTribunaButtonHTML()}
            </div>

            <div style="margin: 0 -1rem 0.55rem; min-height: 40px; padding: 0.5rem 1rem; background: linear-gradient(135deg,#5e93c2 0%,#75aadb 52%,#9bc4e6 100%); box-shadow: 0 16px 34px rgba(117,170,219,.2), 0 0 24px rgba(117,170,219,.22); border-top: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07); display: flex; align-items: center; justify-content: space-between; text-align: left; gap: 0.8rem;">
                <span style="display:flex; align-items:baseline; gap:.5rem; min-width:0; flex:1;">
                    <span style="font-family: var(--font-heading); font-size: .92rem; text-transform: uppercase; color: #06111f; flex:0 0 auto;">&#128227; Arengas</span>
                    <span style="font-family: var(--font-body); font-size: .72rem; letter-spacing: 0; color: rgba(6,17,31,.82); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"><span data-metric="arengas">${formatMetric(metrics.total_arengas_activas)}</span> arengas creadas</span>
                </span>
                <span style="font-size: 1.05rem; color:#06111f; opacity:.9; flex:0 0 auto;">&#127897;</span>
            </div>

            ${publicArengas.length ? `
                <div class="tribuna-wall" aria-label="Muralla de arengas públicas en movimiento">
                    ${Array.from({ length: 4 }, (_, index) => renderAudioWallLane(index)).join('')}
                    ${renderInstitutionalLane()}
                    ${Array.from({ length: 8 }, (_, index) => renderAudioWallLane(index + 4)).join('')}
                </div>
            ` : `
                <div class="glass-card" style="padding: 1rem; margin-bottom: 1rem; text-align: center;">
                    <strong style="display:block;color:var(--white);">Todavía no hay arengas públicas.</strong>
                    <span style="display:block;color:var(--text-muted);font-size:.82rem;margin-top:.3rem;">La tribuna se está preparando.</span>
                    <button class="btn btn-secondary" style="margin: .9rem 0 0;" onclick="window.location.hash='/tribuna-cabalas/${codigo}'">Ver cábalas y promesas</button>
                </div>
            `}


            <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem;margin:.18rem 0 .55rem;">
                <button type="button" class="tribuna-secondary-nav" data-target="cabalas" style="min-height:58px;padding:.52rem .58rem;border-radius:10px;border:1px solid rgba(168,85,247,.18);background:rgba(168,85,247,.07);text-align:center;cursor:pointer;box-shadow:0 8px 18px rgba(168,85,247,.08);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.12rem;">
                    <strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:.78rem;text-transform:uppercase;">&#128302; C&aacute;balas activadas</strong>
                    <span style="display:block;color:var(--text-muted);font-size:.68rem;"><span data-metric="cabalas">${formatMetric(metrics.total_cabalas_activadas)}</span> rituales prendidos</span>
                </button>
                <button type="button" class="tribuna-secondary-nav" data-target="promesas" style="min-height:58px;padding:.52rem .58rem;border-radius:10px;border:1px solid rgba(245,158,11,.18);background:rgba(245,158,11,.07);text-align:center;cursor:pointer;box-shadow:0 8px 18px rgba(245,158,11,.08);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.12rem;">
                    <strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:.78rem;text-transform:uppercase;">&#129310; Promesas activas</strong>
                    <span style="display:block;color:var(--text-muted);font-size:.68rem;"><span data-metric="promesas">${formatMetric(metrics.total_promesas_activas)}</span> apuestas de honor</span>
                </button>
            </div>
            <div style="margin:0 0 1rem;padding:.54rem .68rem;border-radius:999px;border:1px solid rgba(34,197,94,.16);background:rgba(34,197,94,.055);color:#bbf7d0;font-size:.76rem;text-align:center;">
                <span style="width:7px;height:7px;border-radius:50%;background:#22c55e;display:inline-block;margin-right:.38rem;animation:tribunaPulseDot 1.4s ease-in-out infinite;"></span>
                <span data-metric="movidas">${formatMetric(metrics.total_movidas_tribuna)}</span> movidas tribuneras
            </div>

            <!-- Cartel de gate de reacción UNIFICADO (cábala/promesa/arenga idénticos):
                 header "Sumate a jugar" + texto único + 1 botón primario "Entrá a
                 jugar" → home ('/', Pantalla 1, donde se crea grupo / usa código /
                 inicia sesión) + X para cerrar. -->
            <div id="tribunaJoinPrompt" style="display:none;position:fixed;inset:0;z-index:90;background:rgba(0,0,0,.66);backdrop-filter:blur(6px);align-items:flex-end;justify-content:center;padding:.85rem;" onclick="if(event.target === this) this.style.display='none'">
                <div class="glass-card" style="width:min(100%,420px);padding:1rem;border-radius:16px 16px 12px 12px;border-color:rgba(168,85,247,.28);background:radial-gradient(circle at 14% 0%,rgba(168,85,247,.18),transparent 38%),rgba(12,18,30,.98);">
                    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:.8rem;margin-bottom:.8rem;">
                        <div>
                            <strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:1.05rem;line-height:1.05;text-transform:uppercase;">Sumate a jugar</strong>
                            <span style="display:block;color:var(--text-muted);font-size:.8rem;line-height:1.32;margin-top:.2rem;">Para reaccionar, entrá a Pacto Mundial.</span>
                        </div>
                        <button class="btn btn-secondary" type="button" style="width:auto;min-height:32px;margin:0;padding:.32rem .58rem;border-radius:999px;" onclick="document.getElementById('tribunaJoinPrompt').style.display='none'">X</button>
                    </div>
                    <button class="btn btn-primary" type="button" style="margin:0;min-height:46px;padding:.68rem;background:linear-gradient(135deg,var(--celeste),#a855f7);border:none;" onclick="window.location.hash='/'">Entrá a jugar</button>
                </div>
            </div>
        </div>
        ${currentUser ? renderBottomNav(codigo, 'tribuna') : ''}
    `;
    // Contenido real ya en el DOM → bajar la foto (cada vez que se navegó acá).
    hideLoadingOverlay();

    // Cartel de gate unificado: texto único y estático (ya no hay subtítulo
    // configurable). Se conserva el parámetro por compatibilidad con los callers
    // (reaccionar / guardar) pero no altera el contenido.
    const showTribunaJoinPrompt = () => {
        const prompt = document.getElementById('tribunaJoinPrompt');
        if (prompt) prompt.style.display = 'flex';
    };

    // H2 (26-may-2026): los 3 banners CTAs del modo visitante van directo a
    // Pantalla 1 (renderLanding), sin modal intermedio. El modal sigue para
    // los otros casos (reaccionar / guardar / cábalas / promesas) pero sus
    // botones internos también unifican destino a Pantalla 1.
    document.querySelectorAll('.tribuna-join-open').forEach(button => {
        button.addEventListener('click', () => navigate('/'));
    });

    // Cábalas/promesas activas son contenido público igual que las arengas
    // (objetivo Tribuna Pública): el visitante anon navega a la sub-vista y ve
    // el feed. Reaccionar adentro pide registro (cada sub-vista tiene su propio
    // join-prompt). Antes (FASE 3.17 · Bloque D) el anon veía el prompt acá y no
    // llegaba a entrar — se removió ese guard.
    document.querySelectorAll('.tribuna-secondary-nav').forEach(button => {
        button.addEventListener('click', () => {
            const target = button.dataset.target;
            window.location.hash = `/tribuna-${target}/${codigo}`;
        });
    });

    // ── Overlay de arenga (etapa 4): abrir/cerrar montando/desmontando un nodo
    // en document.body, SIN tocar la URL → la muralla nunca se re-renderiza. ──
    const escArenga = (e) => { if (e.key === 'Escape') closeArengaOverlay(); };
    const closeArengaOverlay = () => {
        const overlay = document.querySelector('[data-arenga-detail-open]');
        if (!overlay) return;
        const audio = overlay.querySelector('#tribunaAudio');
        if (audio) { try { audio.pause(); } catch (_) {} revokeBlobUrl(audio); } // no dejar audio sonando ni blob colgado
        document.removeEventListener('keydown', escArenga);
        overlay.remove();
        // Limpiar ?arenga residual del hash (deep-link/arribo) SIN disparar el
        // router (replaceState no emite hashchange) → no reinicia la muralla.
        if (window.location.hash.includes('?arenga=')) {
            const limpio = window.location.hash.split('?')[0];
            history.replaceState(null, '', `${location.pathname}${location.search}${limpio}`);
        }
    };
    const openArengaOverlay = (arenga) => {
        if (!arenga) return;
        closeArengaOverlay(); // un solo overlay vivo a la vez (no apilar nodos)
        const cont = document.createElement('div');
        cont.innerHTML = buildArengaOverlayHTML(arenga);
        const node = cont.firstElementChild;
        document.body.appendChild(node);
        wireArengaOverlay(arenga);
    };

    // Card de la muralla: abre el overlay (sin hash) → muralla intacta. Etapa 3:
    // listener DELEGADO en .tribuna-wall (nodo que persiste los swaps del tick)
    // en vez de uno por card — así las cards rotadas siguen abriendo sin re-wire.
    // Usa publicArengasActual (lo actualiza el tick) para resolver la arenga.
    const wallContainer = document.querySelector('.tribuna-wall');
    if (wallContainer) {
        wallContainer.addEventListener('click', (event) => {
            const button = event.target?.closest?.('.tribuna-audio-open');
            if (!button || !wallContainer.contains(button)) return;
            event.preventDefault();
            event.stopPropagation();
            const arenga = publicArengasActual.find(a => a.id === button.dataset.id);
            if (arenga) openArengaOverlay(arenga);
        });
    }

    // Share de la tribuna entera (28-may-2026) — botón discreto en el header.
    document.getElementById('tribunaShareBtn')?.addEventListener('click', () => {
        openShareModal(buildShareTribunaParams());
    });

    // Wiring de los handlers DEL OVERLAY (react/save/share/audio + cierre). Se
    // ejecuta al montar el overlay (no en el render de la muralla). `selectedArenga`
    // queda como alias del parámetro para reutilizar el cuerpo verbatim.
    const wireArengaOverlay = (arenga) => {
        const selectedArenga = arenga;
    document.querySelectorAll('.tribuna-audio-react').forEach(button => {
        button.addEventListener('click', async () => {
            if (button.disabled) return;
            if (!canInteractPublic || !jugadorActivo) {
                showTribunaJoinPrompt('reaccionar');
                return;
            }
            const reaction = button.dataset.reaction;
            const isActive = button.dataset.active === '1';
            const arengaId = button.dataset.id;
            // Spec arengas v1.2 §9: al_pasto = 1 reporte por jugador. La DB
            // tiene UNIQUE constraint que ignora duplicados; el front bloquea
            // para que el contador optimistic no se infle con clicks repetidos.
            if (reaction === 'pasto' && isActive) return;
            // Optimistic: spec arengas v1.2 §10 reglas 3-4. Aplicar delta al
            // DOM antes del RPC, revertir si falla. El reconcile (cache invalid +
            // re-render) viene después del RPC OK — Tribuna además requiere
            // invalidarCacheTribuna() porque el feed_tribuna RPC se cachea aparte.
            const prevActive = document.querySelector(`.tribuna-audio-react[data-id="${arengaId}"][data-active="1"]`);
            const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null;
            const deltas = computeReactionDelta({
                clickedReaction: reaction,
                isActiveOnClicked: isActive,
                prevActiveReaction,
            });
            applyReactionDelta({ scope: document, selector: '.tribuna-audio-react', arengaId, deltas });
            const sumaDeltaAT = deltas.reduce((s, d) => s + d.delta, 0);
            refrescarContadoresRitual('arenga', arengaId, sumaDeltaAT);
            button.disabled = true;
            button.style.opacity = '.5';
            let r;
            if (reaction === 'pasto') {
                r = await reaccionar(jugadorActivo.id, 'arenga', arengaId, 'al_pasto');
            } else if (isActive) {
                r = await quitarReaccion(jugadorActivo.id, 'arenga', arengaId);
            } else {
                r = await reaccionar(jugadorActivo.id, 'arenga', arengaId, reaction);
            }
            if (r.error) {
                applyReactionDelta({ scope: document, selector: '.tribuna-audio-react', arengaId, deltas: invertDeltas(deltas) });
                refrescarContadoresRitual('arenga', arengaId, -sumaDeltaAT);
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorTribuna(r.error));
                return;
            }
            // Sin re-render full: el optimistic + invalidación de cache alcanzan.
            // El render fresco lo hace la próxima visita al módulo. El re-render
            // que estaba acá cerraba/reabría el modal causando flash.
            invalidarCacheTribuna();
            // Reconciliar el MODELO en memoria (sin fetch extra): el conteo del
            // tipo + el estado "mi reacción" quedan coherentes para los
            // re-renders que pintan desde el objeto (reabrir el modal de detalle,
            // rebuild de la muralla). Sin esto, el optimistic solo tocaba el DOM
            // y al re-renderizar desde el objeto stale el contador "revertía" al
            // valor previo (bug arengas cross-group, 8-jun-2026). El feed real
            // converge solo por su TTL/realtime. Cubre selectedArenga (modal
            // abierto) + ambos arrays vivos (pueden reasignarse por la rotación).
            const targetsReconcile = new Set([selectedArenga]);
            for (const m of rankedActual) targetsReconcile.add(m);
            for (const m of publicArengasActual) targetsReconcile.add(m);
            for (const m of targetsReconcile) {
                if (m && m.id === arengaId) {
                    applyReactionToModel(m, deltas, { clickedReaction: reaction, isActiveOnClicked: isActive });
                }
            }
            button.disabled = false;
            button.style.opacity = '1';
            if (reaction === 'pasto') {
                button.dataset.active = '1';
                button.style.background = 'rgba(192,132,252,0.22)';
                button.style.borderColor = 'rgba(192,132,252,0.6)';
                button.style.boxShadow = '0 0 0 1px rgba(192,132,252,.35) inset';
                const labelSpan = button.querySelector('span');
                if (labelSpan) labelSpan.textContent = '✓ Reportado';
            } else {
                document.querySelectorAll(`.tribuna-audio-react[data-id="${arengaId}"]`).forEach((b) => {
                    if (b.dataset.reaction === 'pasto') return;
                    b.dataset.active = (b === button && !isActive) ? '1' : '0';
                });
            }
        });
    });

    document.querySelectorAll('.tribuna-audio-save').forEach(button => {
        button.addEventListener('click', async () => {
            if (button.disabled) return;
            if (!canInteractPublic) {
                showTribunaJoinPrompt('guardar');
                return;
            }
            const arengaId = button.dataset.id;
            const isSaved = button.dataset.saved === '1';
            // Optimistic: cambiar texto y data-saved antes del RPC.
            button.dataset.saved = isSaved ? '0' : '1';
            button.textContent = isSaved ? 'Guardar arenga' : '✓ Guardada';
            button.disabled = true;
            button.style.opacity = '.5';
            const r = isSaved
                ? await quitarArengaGuardada(currentUser.user_id, arengaId)
                : await guardarArenga(currentUser.user_id, arengaId);
            if (r.error) {
                button.dataset.saved = isSaved ? '1' : '0';
                button.textContent = isSaved ? '✓ Guardada' : 'Guardar arenga';
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorTribuna(r.error));
                return;
            }
            // DT-tribuna-save-rerender cerrada (etapa 3): SIN renderTribuna full
            // (remontaba la muralla = reinicio del marquee con el overlay abierto).
            // El optimistic ya pintó el botón; actualizamos el savedSet local para
            // que reabrir la arenga refleje el estado, e invalidamos la cache para
            // que la próxima visita traiga el dato fresco. Mismo patrón que el
            // handler de reacción.
            if (isSaved) savedSet.delete(arengaId); else savedSet.add(arengaId);
            button.disabled = false;
            button.style.opacity = '1';
            invalidarCacheTribuna();
        });
    });

    // Spec arengas v1.2 §8: share unificado — abre el mismo modal que
    // "Invitar amigos" del grupo (WhatsApp / Telegram / Copiar / Cancelar).
    // Reemplaza el viejo flow con navigator.share + fallback a clipboard.
    document.querySelectorAll('.tribuna-audio-share').forEach(button => {
        button.addEventListener('click', () => {
            const audioId = button.dataset.audioId;
            if (!audioId) return;
            const titulo = button.dataset.titulo || 'arenga mundialista';
            const creador = button.dataset.creador || 'la tribuna';
            const url = buildShareUrl(audioId);
            openShareModal({
                title: `Compartir arenga · ${titulo}`,
                subtitle: `De ${creador}`,
                shareUrl: url,
                shareText: `Escuchá la arenga de ${creador}: "${titulo}"`,
            });
        });
    });

    // F1.4.a: <audio> real con preload="metadata". Si no hay audio_url, el
    // botón queda disabled desde el HTML (arenga sin audio subido aún).
    const tribunaAudio = document.getElementById('tribunaAudio');
    const tribunaAudioPlay = document.getElementById('tribunaAudioPlay');
    const tribunaAudioProgress = document.getElementById('tribunaAudioProgress');
    if (tribunaAudio && tribunaAudioPlay && tribunaAudioProgress && selectedArenga) {
        // Bug B v2: para .webm con Duration=0 explícito en EBML, el browser
        // reproduce el contenido (audio suena) pero NO transiciona a
        // "playing state": no dispara el evento 'play', no actualiza
        // audio.currentTime ni audio.duration. El fix v1 enganchaba el rAF
        // a 'play' → nunca arrancaba para estos archivos. v2: arrancamos
        // rAF desde el click handler y trackeamos elapsed con wall-clock
        // (Date.now) cuando currentTime no avanza.
        let progressRafId = null;
        let progressStartMs = 0;   // wall-clock al iniciar/resumir play
        let progressAccumMs = 0;   // tiempo acumulado (resume tras pause)
        const stopProgress = () => {
            if (progressRafId !== null) { cancelAnimationFrame(progressRafId); progressRafId = null; }
        };
        const cleanupBlob = () => revokeBlobUrl(tribunaAudio);
        const finalizeEnded = () => {
            try { tribunaAudio.pause(); } catch (_) {}
            stopProgress();
            progressStartMs = 0;
            progressAccumMs = 0;
            tribunaAudioPlay.textContent = 'Play';
            tribunaAudioProgress.style.width = '0%';
            cleanupBlob();
        };
        const updateProgress = () => {
            const dur = tribunaAudio.duration;
            const ct = tribunaAudio.currentTime;
            let elapsed, total;
            if (Number.isFinite(dur) && dur > 0 && ct > 0) {
                elapsed = ct; total = dur;
            } else {
                const live = progressStartMs > 0 ? (Date.now() - progressStartMs) : 0;
                elapsed = (progressAccumMs + live) / 1000;
                total = selectedArenga.segundos || 1;
            }
            const pct = Math.min(100, (elapsed / total) * 100);
            tribunaAudioProgress.style.width = `${pct}%`;
            // Auto-ended: archivos con Duration=0 tampoco disparan 'ended'.
            if (pct >= 100 && (!Number.isFinite(dur) || dur <= 0)) {
                finalizeEnded();
            }
        };
        const tickProgress = () => {
            updateProgress();
            if (progressRafId !== null && !tribunaAudio.paused && !tribunaAudio.ended) {
                progressRafId = requestAnimationFrame(tickProgress);
            }
        };
        tribunaAudio.addEventListener('pause', () => {
            stopProgress();
            if (progressStartMs > 0) { progressAccumMs += Date.now() - progressStartMs; progressStartMs = 0; }
        });
        tribunaAudio.addEventListener('ended', finalizeEnded);
        tribunaAudioPlay.addEventListener('click', async () => {
            if (tribunaAudio.paused) {
                // Cap de arengas para ANÓNIMO (APAGADO por default). Solo cuando
                // arranca una reproducción (paused→play) y solo sin login. Cuenta
                // arengas DISTINTAS por audio_id; al llegar al tope, la próxima
                // arenga NUEVA muestra el cartel unificado en vez de reproducir.
                // Re-escuchar una ya contada o pausar/reanudar nunca se bloquea.
                if (!canInteractPublic && capArengasFlag?.enabled) {
                    const audioId = selectedArenga.audioId;
                    if (!puedeEscucharArengaAnon(audioId, capArengasFlag)) {
                        showTribunaJoinPrompt();
                        return;
                    }
                    registrarEscuchaAnon(audioId);
                }
                tribunaAudioPlay.textContent = 'Pausa';
                // Bug Duration=0 v3: bajar el archivo a un blob URL local
                // antes del play. Helper compartido en core/audio-player.js
                // (defensivo: si falla, sigue con src original).
                const blobResult = await ensureBlobUrl(tribunaAudio);
                if (blobResult.reason === 'timeout') {
                    // Conexión muerta (mobile): no insistir con el play (fallaría
                    // igual). Aviso claro: el botón pasa a "Reintentar" y el próximo
                    // tap vuelve a probar la descarga.
                    console.warn('[tribuna] audio timeout');
                    tribunaAudioPlay.textContent = 'Reintentar';
                    stopProgress();
                    return;
                }
                if (!blobResult.ok && !blobResult.cached) {
                    console.warn('[tribuna] ensureBlobUrl:', blobResult.reason);
                }
                // Bug PROD player: <audio> en innerHTML + display:none puede
                // quedar en readyState=0 sin disparar el resource selection
                // algorithm. .load() lo fuerza; idempotente.
                if (tribunaAudio.readyState === 0) tribunaAudio.load();
                const p = tribunaAudio.play();
                if (p && typeof p.catch === 'function') p.catch((err) => {
                    console.error('[tribuna] audio.play():', err);
                    tribunaAudioPlay.textContent = 'Play';
                    stopProgress();
                    progressStartMs = 0;
                });
                // Bug B v2: arrancar rAF desde el click — para archivos con
                // Duration=0 el evento 'play' nunca se dispara.
                progressStartMs = Date.now();
                if (progressRafId === null) progressRafId = requestAnimationFrame(tickProgress);
            } else {
                tribunaAudio.pause();
                tribunaAudioPlay.textContent = 'Play';
            }
        });
    }

        // Cierre del overlay: X, tap-afuera y ESC — sin tocar la URL.
        const overlayNode = document.querySelector('[data-arenga-detail-open]');
        if (overlayNode) {
            overlayNode.addEventListener('click', (e) => { if (e.target === overlayNode) closeArengaOverlay(); });
            overlayNode.querySelector('[data-arenga-close]')?.addEventListener('click', closeArengaOverlay);
        }
        document.addEventListener('keydown', escArenga);
    };

    // Arribo inicial / deep-link (/t/AUDIO_ID → destacarArenga, o ?arenga en el
    // hash): montar el overlay por el MISMO path, sin re-render de la muralla.
    if (selectedArenga) openArengaOverlay(selectedArenga);

    // Etapa 3 — helpers de update IN-PLACE (sin remontar la vista).
    const sigueEnTribuna = () => esRutaConRenderTribuna(window.location.hash);
    // setMetric: cambia el número de una métrica sin tocar el resto del DOM.
    const setMetric = (key, value) => {
        const el = document.querySelector(`[data-metric="${key}"]`);
        if (el) el.textContent = formatMetric(value);
    };
    // Re-fetch de las métricas globales (cábalas/promesas/jugadores) + arengas
    // (= largo del feed actual). Tras invalidarCacheTribuna() las RPC traen
    // valores frescos. Se llama desde el realtime estructural.
    const actualizarMetricas = async () => {
        const [a, c, p, r] = await Promise.all([
            contarArengasTotal(), contarCabalasActivas(), contarPromesasActivas(), contarReaccionesTotal(),
        ]);
        if (!sigueEnTribuna()) return;
        setMetric('arengas', a);
        setMetric('cabalas', c);
        setMetric('promesas', p);
        setMetric('movidas', a + c + p + r);
    };

    // perf-tribuna vidriera (31-may): renovación periódica (~2min). Re-fetchea las
    // 500 con score C fresco, arma una NUEVA selección (bombo + destacados,
    // barajada) y hace el swap IN-PLACE de los hijos de cada .tribuna-wall-strip.
    // El nodo strip NO se reemplaza → el marquee no reinicia. cardsPorLane es FIJO
    // (8) → ancho constante → el -33.333% sigue seamless. Los 12 swaps van en un
    // solo frame sincrónico (atómico, sin flash).
    const rotarMuralla = async () => {
        if (!sigueEnTribuna()) { // el user salió de la tribuna → auto-cancelar
            if (_rotacionTimer) { clearInterval(_rotacionTimer); _rotacionTimer = null; }
            return;
        }
        invalidarCacheTribuna();
        let fresh;
        try { fresh = await getFeedTribuna('arenga', { limit: TRIBUNA_WINDOW }); }
        catch (err) { console.error('[tribuna] rotarMuralla feed falló:', err?.message || err); return; } // mantiene lo pintado; reintenta el próximo tick
        if (!sigueEnTribuna()) return;
        const strips = document.querySelectorAll('.tribuna-wall-strip');
        if (!strips.length) return;
        const freshPublic = (fresh || []).map(mapFeedArenga);
        // Si el feed quedó vacío, NO tocamos los strips (se mantiene lo previo).
        if (!freshPublic.length) return;
        rankedActual = freshPublic;
        publicArengasActual = freshPublic;
        filasActuales = construirFilas(); // nueva vidriera barajada (bombo avanza)
        strips.forEach((strip, i) => { strip.innerHTML = buildLaneInner(i); });
        // El contador de arengas sale del RPC (universo amplio), NO del largo del
        // feed. invalidarCacheTribuna() arriba ya limpió la cuenta → trae fresco.
        setMetric('arengas', await contarArengasTotal());
    };

    // F1.4.b · Realtime de la Tribuna — ETAPA 3: update IN-PLACE, NUNCA
    // renderTribuna full (remontaba la muralla = reinicio del marquee). Las
    // reacciones bumpean los contadores de la card afectada; los eventos
    // estructurales (alta/baja de arenga/cábala/promesa/usuario) refrescan las 4
    // métricas. La aparición de arengas nuevas en la muralla la maneja el tick
    // periódico (≤5min): DT-tribuna-nueva-arenga-5min. Se skipea el cambio propio
    // (el optimistic local ya pintó el delta).
    const onReaccionRealtime = (payload) => {
        if (!sigueEnTribuna()) return;
        invalidarCacheTribuna();
        const cambioPropio = payload?.new?.player_id && jugadorActivo?.id &&
            payload.new.player_id === jugadorActivo.id;
        if (cambioPropio) return;
        if (payload?.new?.ritual_tipo === 'arenga' && payload?.new?.ritual_id != null) {
            refrescarContadoresRitual('arenga', String(payload.new.ritual_id), 1);
        }
    };
    const onEstructuralRealtime = () => {
        if (!sigueEnTribuna()) return;
        invalidarCacheTribuna();
        actualizarMetricas();
    };
    subscribirCambios('tribuna', 'arengas', { event: '*' }, onEstructuralRealtime);
    subscribirCambios('tribuna', 'reacciones_ritual', { event: 'INSERT' }, onReaccionRealtime);
    subscribirCambios('tribuna', 'cabalas', { event: '*' }, onEstructuralRealtime);
    subscribirCambios('tribuna', 'promesas', { event: '*' }, onEstructuralRealtime);
    // Caveat RLS: la suscripción a `usuarios` solo dispara eventos por filas
    // visibles para el usuario (RLS = mi fila + quienes comparten grupo). Si
    // se registra un usuario que no comparte grupo, el contador se actualiza
    // en el próximo tick. Aceptable para piloto; mejora real con tabla agregada
    // `metricas_globales` queda como DT post-25-may.
    subscribirCambios('tribuna', 'usuarios', { event: 'INSERT' }, onEstructuralRealtime);

    // perf-tribuna (30-may + vidriera 31-may): carga de fondo de la ventana
    // completa (500). El primer pintado ya ocurrió con 96 (la muralla está
    // rotando). Ahora, sin bloquear, traemos las 500 para que la vidriera elija
    // sobre el pool completo, recomputamos las my-reaction sobre TODAS (cobertura
    // idéntica) y ampliamos rankedActual. NO se hace swap de strips: la selección
    // visible se mantiene hasta la próxima renovación (~2min) → cero repaint, cero
    // parpadeo. El pool ampliado lo consume la vidriera en los renovados siguientes
    // (el bombo barre las 500).
    const cargarVentanaCompletaDeFondo = async () => {
        if (publicArengas.length < TOTAL_VISIBLE) return; // ya entró todo en el primer fetch
        let full;
        try { full = await getFeedTribuna('arenga', { limit: TRIBUNA_WINDOW }); }
        catch (err) { console.error('[tribuna] cargarVentanaCompletaDeFondo feed falló:', err?.message || err); return; }
        if (!sigueEnTribuna()) return;
        const fullIds = (full || []).map(a => a.arenga_id);
        if (fullIds.length <= publicArengas.length) return; // no hay más que lo ya cargado
        // Recomputar las marcas del jugador sobre la ventana completa (paralelo) y
        // reasignar los closures que lee mapFeedArenga → las cards de futuras
        // renovaciones quedan marcadas igual que hoy.
        if (jugadorActivo?.id) {
            const [reac, pasto] = await Promise.all([
                getMisReaccionesArengas(jugadorActivo.id, fullIds),
                getMisAlPastoArengas(jugadorActivo.id, fullIds),
            ]);
            if (!sigueEnTribuna()) return;
            miReaccionMap = reac;
            miAlPastoSet = pasto;
        }
        const fullPublic = full.map(mapFeedArenga);
        // Preservar el prepend de la arenga destacada (link viral) si no vino en
        // la ventana — mismo criterio que el fetch inicial.
        if (opts.destacarArenga && !fullPublic.find(a => a.id === opts.destacarArenga.arenga_id)) {
            const existente = publicArengasActual.find(a => a.id === opts.destacarArenga.arenga_id);
            if (existente) fullPublic.unshift(existente);
        }
        if (!fullPublic.length) return;
        // Ampliar el pool SIN tocar los strips: la selección visible se mantiene
        // hasta la próxima renovación (~2min). El bombo barrerá las 500 desde ahí.
        rankedActual = fullPublic;
        publicArengasActual = fullPublic;
    };
    cargarVentanaCompletaDeFondo();

    // Etapa 3 — arranque del refresco periódico in-place (clear previo arriba).
    _rotacionTimer = setInterval(rotarMuralla, TRIBUNA_REFRESCO_MS);
};

// TODO 3.12-followup: renderTribunaArengas es código muerto del prototipo
// original (vista mock con 12 arengas hardcodeadas "VAMOS ARG!" en grilla).
// El dueño confirmó en smoke PRE del 19-may que esta pantalla NO vive en
// ninguna visualización de la app actual — ningún botón ni link la
// dispatchea. Eliminación CONFIRMADA, pendiente de sub-fase de limpieza
// post-3.12. Borrar junto con:
//   - la ruta /tribuna-arengas/<codigo> en src/core/router.js:72-74
//   - el bridge window.renderTribunaArengas (línea original 2467 de js/app.js)
//   - el export const renderTribunaArengas de este módulo
export const renderTribunaArengas = (codigo) => {
    const arengas = Array.from({length: 12}, (_, i) => ({
        id: i,
        titulo: 'VAMOS ARG!',
        duracion: '0:1' + (i % 9 + 2),
        creador: 'Dibu' + i,
        grupo: 'Los Pibes'
    }));

    appRoot.innerHTML = `
        <div class="view-enter" style="width: 100%; animation: fadeIn 0.4s ease-out forwards;">
            ${renderActiveGroupBar(codigo)}
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
                <button class="btn btn-secondary" style="padding: 0.5rem 1rem; margin: 0; width: auto;" onclick="window.history.back()">←</button>
                <h2 style="font-family: var(--font-heading); font-size: 1.5rem; text-transform: uppercase; color: var(--white); margin: 0;">📣 Arengas</h2>
            </div>

            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.8rem; margin-bottom: 2rem;">
                ${arengas.map(a => `
                    <div class="glass-card arenga-item" title="De: ${a.creador} (${a.grupo})" style="padding: 1rem 0.5rem; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; cursor: pointer; aspect-ratio: 1; background: rgba(255,255,255,0.05);" onclick="alert('Reproduciendo arenga de ${a.creador}...')">
                        <span style="font-size: 1.5rem;">▶️</span>
                        <strong style="font-size: 0.75rem; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%; color: var(--celeste);">${a.titulo}</strong>
                        <span style="font-size: 0.65rem; color: var(--text-muted);">${a.duracion}</span>
                    </div>
                `).join('')}
            </div>
        </div>
        ${renderBottomNav(codigo, 'tribuna')}
    `;
};

export const renderTribunaCabalas = async (codigo) => {
    // Cábalas activas son contenido público igual que las arengas: el visitante
    // anon ve el feed. Reaccionar adentro pide registro (showCabalaJoinPrompt).
    // El feed sale del RPC feed_tribuna (SECURITY DEFINER, GRANT anon), no de
    // una consulta gateada por RLS. Antes (FASE 3.17 · Bloque D) el anon se
    // redirigía a /tribuna — se removió ese guard.
    const now = Date.now();
    const db = getNormalizedDB();
    const grupo = db.grupos[codigo] || { nombre: 'PACTO MUNDIAL' };
    const currentUser = getCurrentUser();
    const jugadorActivo = getMembership(codigo);
    const selectedCabalaId = (window.location.hash.split('?')[0].split('/')[3] || '').trim();
    const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, char => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
    }[char]));

    appRoot.innerHTML = buildTribunaCabalasSkeleton();
    limpiarSuscripciones('tribuna-cabalas');

    let feed;
    try { feed = await getFeedTribuna('cabala', { limit: 60 }); }
    catch (err) {
        console.error('[tribuna-cabalas] feed falló:', err?.message || err);
        if (!window.location.hash.startsWith('#/tribuna-cabalas/')) return;
        appRoot.innerHTML = buildTribunaErrorHTML(codigo, currentUser, 'No se pudieron cargar las cábalas');
        appRoot.querySelector('[data-tribuna-retry]')?.addEventListener('click', () => renderTribunaCabalas(codigo));
        return;
    }
    if (!window.location.hash.startsWith('#/tribuna-cabalas/')) return;

    const reactionTotal = (c, reaction) => {
        if (reaction === 'bendita_sea') return (c.bendicionesGrupo || 0) + (c.bendicionesPublicas || 0);
        if (reaction === 'me_sumo') return (c.sumadosGrupo || 0) + (c.sumadosPublicos || 0);
        return 0;
    };
    // Auditoría 28-may: cargar mi reacción a cada cábala del feed (1 por
    // jugador, doc §5). Marca el botón activo y el handler decide reemplazo
    // (RPC ya hace el DELETE de la previa al insertar la nueva).
    const feedCabalaIds = (feed || []).map(c => c.cabala_id);
    const miReaccionCabalaMap = (jugadorActivo?.id && feedCabalaIds.length)
        ? await getMisReacciones(jugadorActivo.id, 'cabala', feedCabalaIds)
        : {};
    // Feed público de cábalas (tribuna_feed) adaptado al shape legacy.
    const publicCabalas = feed.map(c => {
        const reac = c.reacciones || {};
        return {
            id: c.cabala_id,
            texto: c.texto,
            descripcion: c.texto,
            creadorId: c.creador_id,
            creador: c.creador_nombre || 'Jugador',
            grupoOrigen: c.grupo_nombre || 'Tribuna',
            visibilidad: 'publica',
            duracion: '',
            createdAt: new Date(c.created_at).getTime(),
            expiresAt: new Date(c.expira_en).getTime(),
            bendicionesPublicas: reac.bendita_sea || 0,
            sumadosPublicos: reac.me_sumo || 0,
            miReaccion: miReaccionCabalaMap[c.cabala_id] || null,
            aprobacionPublica: 'aprobada',
        };
    });
    const selectedCabala = publicCabalas.find(c => c.id === selectedCabalaId);
    // TODO 3.12-followup: extraer como formatRemainingCabala / formatRemainingPromesa
    // al nivel del módulo. Hoy son 2 declaraciones locales con misma lógica matemática
    // pero distinto texto de output ('quedan X' vs 'X restantes'). Diferido a sub-fase
    // dedicada de limpieza interna de tribuna.js.
    const formatRemaining = (expiresAt) => {
        const minutes = Math.ceil(Math.max(0, expiresAt - Date.now()) / 60000);
        if (minutes < 60) return `quedan ${minutes} min`;
        const hours = Math.ceil(minutes / 60);
        if (hours < 24) return `quedan ${hours} h`;
        const days = Math.ceil(hours / 24);
        return days === 1 ? 'queda 1 dia' : `quedan ${days} dias`;
    };
    const timeAgo = (createdAt) => formatTimeAgo(createdAt);
    const isOwnCabalaPub = (cabala) => jugadorActivo?.id && cabala.creadorId
        && jugadorActivo.id.toString() === cabala.creadorId.toString();
    const renderCabalaCard = (cabala, index) => {
        const tilt = [-1.4, 0.8, -0.5, 1.2, -0.9, 0.4][index % 6];
        const offset = [0, 8, -3, 12, 4, -6][index % 6];
        const minHeight = [146, 166, 136, 156, 174, 144][index % 6];
        const mineC = isOwnCabalaPub(cabala);
        const btn = (reactId, label) => {
            const active = !mineC && cabala.miReaccion === reactId;
            const cls = mineC ? '' : ' tribuna-cabala-react';
            return `<button type="button" class="${cls.trim()}" data-id="${cabala.id}" data-reaction="${reactId}" data-active="${active ? '1' : '0'}" ${mineC ? 'aria-disabled="true" style="opacity:.55;cursor:default;"' : (active ? 'style="background:rgba(168,85,247,.24);border-color:rgba(168,85,247,.55);"' : '')}>
                <span>${label}</span>
                <span data-cabala-count="${cabala.id}:${reactId}">${reactionTotal(cabala, reactId)}</span>
            </button>`;
        };
        return `
            <div class="tribuna-cabala-card" data-id="${cabala.id}" style="min-height:${minHeight}px;transform:rotate(${tilt}deg) translateY(${offset}px);animation-delay:${index * 0.09}s;cursor:default;">
                <span class="tribuna-cabala-state">Activa &middot; ${formatRemaining(cabala.expiresAt)}</span>
                <strong>${escapeHTML(cabala.texto || 'Cabala activa')}</strong>
                <span class="tribuna-cabala-origin">${escapeHTML(cabala.creador || 'Jugador')} &middot; ${escapeHTML(cabala.grupoOrigen || grupo.nombre)}${mineC ? ' &middot; tu cábala' : ''}</span>
                ${(() => { const t = (cabala.bendicionesPublicas||0)+(cabala.sumadosPublicos||0); const p = puntosPorRitual({ reaccionesTotal: t }); return `<span data-rit-pts="cabala:${cabala.id}" data-rit-reac="${t}" style="position:relative;color:#7ab488;font-size:.62rem;">Puntos: ${p>0?'+':''}${p}</span>`; })()}
                <div class="tribuna-cabala-reactions">
                    ${btn('bendita_sea', '&#128591; Bendita sea')}
                    ${btn('me_sumo', '&#129309; Me sumo')}
                </div>
            </div>
        `;
    };
    const renderCabalaLane = (laneIndex) => {
        const laneItems = [...publicCabalas]
            .sort((a, b) => ((a.id + laneIndex) > (b.id + laneIndex) ? 1 : -1))
            .map((cabala, index) => renderCabalaCard(cabala, index + laneIndex));
        const reverse = laneIndex % 2 === 1;
        // 30-may: acelerado de 88+12k → 30+5k (≈3x más rápido). Reportado
        // como "muy lento" por dueño. Sigue conservador (cards legibles).
        const duration = 30 + (laneIndex * 5);
        return `<div class="tribuna-cabala-lane" style="--duration:${duration}s;"><div class="tribuna-cabala-track ${reverse ? 'reverse' : ''}">${[laneItems.join(''), laneItems.join('')].join('')}</div></div>`;
    };
    const renderCabalaDetail = (cabala) => cabala ? `
        <div class="tribuna-cabala-overlay" onclick="if(event.target === this) window.location.hash='/tribuna-cabalas/${codigo}'">
            <div class="tribuna-cabala-modal">
                <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:.8rem;margin-bottom:.85rem;">
                    <div style="min-width:0;">
                        <span style="display:inline-flex;align-items:center;gap:.3rem;color:#f0abfc;background:rgba(168,85,247,.12);border:1px solid rgba(168,85,247,.28);border-radius:999px;padding:.28rem .58rem;font-size:.66rem;font-weight:950;text-transform:uppercase;">&#128302; C&aacute;bala p&uacute;blica</span>
                        <h3 style="margin:.55rem 0 0;color:var(--white);font-family:var(--font-heading);font-size:1.24rem;line-height:1.02;text-transform:uppercase;">${escapeHTML(cabala.texto || 'Cabala activa')}</h3>
                    </div>
                    <button class="btn btn-secondary" type="button" style="width:auto;min-height:32px;margin:0;padding:.34rem .58rem;border-radius:999px;" onclick="window.location.hash='/tribuna-cabalas/${codigo}'">X</button>
                </div>
                <div style="padding:.85rem;border-radius:14px;background:radial-gradient(circle at 12% 0%, rgba(168,85,247,.22), transparent 38%), rgba(0,0,0,.18);border:1px solid rgba(255,255,255,.1);box-shadow:inset 0 1px 0 rgba(255,255,255,.08);">
                    <p style="margin:0;color:rgba(255,255,255,.9);font-size:.95rem;line-height:1.36;font-weight:800;">${escapeHTML(cabala.descripcion || cabala.texto || 'Ritual activo de la tribuna.')}</p>
                </div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:.48rem;margin-top:.7rem;">
                    <div class="tribuna-cabala-fact"><span>Jugador</span><strong>${escapeHTML(cabala.creador || 'Jugador')}</strong></div>
                    <div class="tribuna-cabala-fact"><span>Grupo</span><strong>${escapeHTML(cabala.grupoOrigen || grupo.nombre)}</strong></div>
                    <div class="tribuna-cabala-fact"><span>Duraci&oacute;n</span><strong>${escapeHTML(cabala.duracion || 'Ritual activo')}</strong></div>
                    <div class="tribuna-cabala-fact"><span>Tiempo</span><strong>${formatRemaining(cabala.expiresAt)}</strong></div>
                </div>
                <div style="margin-top:.62rem;color:var(--text-muted);font-size:.74rem;">Activada ${timeAgo(cabala.createdAt || now)} &middot; conserva origen en ${escapeHTML(cabala.grupoOrigen || grupo.nombre)}</div>
                ${isOwnCabalaPub(cabala) ? `
                    <div style="margin-top:.85rem;padding:.5rem;border-radius:11px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.7rem;text-align:center;">Es tu cábala — no podés reaccionar a vos mismo.</div>
                    <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem;margin-top:.42rem;">
                        <div aria-disabled="true" style="margin:0;min-height:48px;border-radius:14px;background:rgba(168,85,247,.06);border:1px solid rgba(168,85,247,.18);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.12rem;opacity:.55;cursor:default;"><span style="color:var(--white);font-weight:950;">&#128591; Bendita sea</span><span style="color:#f0abfc;font-size:.78rem;font-weight:950;">${reactionTotal(cabala, 'bendita_sea')}</span></div>
                        <div aria-disabled="true" style="margin:0;min-height:48px;border-radius:14px;background:rgba(245,158,11,.06);border:1px solid rgba(245,158,11,.18);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.12rem;opacity:.55;cursor:default;"><span style="color:var(--white);font-weight:950;">&#129309; Me sumo</span><span style="color:#fde68a;font-size:.78rem;font-weight:950;">${reactionTotal(cabala, 'me_sumo')}</span></div>
                    </div>
                ` : `
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem;margin-top:.85rem;">
                    <button class="btn btn-secondary tribuna-cabala-react" data-id="${cabala.id}" data-reaction="bendita_sea" data-active="${cabala.miReaccion === 'bendita_sea' ? '1' : '0'}" style="margin:0;min-height:58px;border-radius:14px;background:rgba(168,85,247,${cabala.miReaccion === 'bendita_sea' ? '.28' : '.12'});border-color:rgba(168,85,247,${cabala.miReaccion === 'bendita_sea' ? '.55' : '.28'});${cabala.miReaccion === 'bendita_sea' ? 'box-shadow:0 0 0 1px rgba(168,85,247,.4) inset;' : ''}display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.12rem;"><span style="color:var(--white);font-weight:950;">&#128591; Bendita sea</span><span data-cabala-count="${cabala.id}:bendita_sea" style="color:#f0abfc;font-size:.78rem;font-weight:950;">${reactionTotal(cabala, 'bendita_sea')}</span></button>
                    <button class="btn btn-secondary tribuna-cabala-react" data-id="${cabala.id}" data-reaction="me_sumo" data-active="${cabala.miReaccion === 'me_sumo' ? '1' : '0'}" style="margin:0;min-height:58px;border-radius:14px;background:rgba(245,158,11,${cabala.miReaccion === 'me_sumo' ? '.28' : '.12'});border-color:rgba(245,158,11,${cabala.miReaccion === 'me_sumo' ? '.55' : '.3'});${cabala.miReaccion === 'me_sumo' ? 'box-shadow:0 0 0 1px rgba(245,158,11,.4) inset;' : ''}display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.12rem;"><span style="color:var(--white);font-weight:950;">&#129309; Me sumo</span><span data-cabala-count="${cabala.id}:me_sumo" style="color:#fde68a;font-size:.78rem;font-weight:950;">${reactionTotal(cabala, 'me_sumo')}</span></button>
                </div>
                `}
            </div>
        </div>
    ` : '';

    appRoot.innerHTML = `
        <div class="view-enter" style="width: 100%; animation: fadeIn 0.4s ease-out forwards;">
            <style>
                @keyframes tribunaCabalaAppear { from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; } }
                @keyframes tribunaCabalaFloat { 0%, 100% { filter: brightness(1); } 50% { filter: brightness(1.08); } }
                @keyframes tribunaCabalaMarquee { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }
                @keyframes tribunaCabalaMarqueeReverse { from { transform: translate3d(-50%,0,0); } to { transform: translate3d(0,0,0); } }
                .tribuna-cabala-mosaic { overflow:hidden; margin: .72rem -1rem 1.25rem; padding:.12rem 0 .28rem; mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%); display:grid; gap:.46rem; }
                .tribuna-cabala-lane { overflow:hidden; padding:0 1rem; }
                .tribuna-cabala-track { display:flex; align-items:stretch; gap:.58rem; width:max-content; will-change:transform; animation:tribunaCabalaMarquee var(--duration, 30s) linear infinite; }
                .tribuna-cabala-track.reverse { animation-name:tribunaCabalaMarqueeReverse; }
                .tribuna-cabala-track:hover, .tribuna-cabala-track:focus-within { animation-play-state: paused; }
                .tribuna-cabala-card { flex:0 0 172px; width:172px; margin: 0; padding: .78rem; border-radius: 14px; border: 1px solid rgba(168,85,247,.26); background: radial-gradient(circle at 18% 0%, rgba(168,85,247,.28), transparent 42%), radial-gradient(circle at 100% 100%, rgba(245,158,11,.13), transparent 36%), linear-gradient(145deg, rgba(17,24,39,.96), rgba(10,14,24,.98)); color: var(--white); text-align: left; cursor: pointer; box-shadow: 0 13px 28px rgba(0,0,0,.34), 0 0 22px rgba(168,85,247,.12), inset 0 1px 0 rgba(255,255,255,.08); display: inline-flex; flex-direction: column; gap: .48rem; position: relative; overflow: hidden; animation: tribunaCabalaAppear .42s ease both, tribunaCabalaFloat 4.6s ease-in-out infinite; transition: transform .32s ease, filter .24s ease, border-color .24s ease; pointer-events:auto; }
                .tribuna-cabala-card::before { content: ''; position: absolute; inset: 0; background-image: linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px); background-size: 100% 13px; opacity: .22; pointer-events: none; }
                .tribuna-cabala-card strong { position: relative; display: block; font-family: var(--font-heading); font-size: .98rem; line-height: 1.08; text-transform: uppercase; color: var(--white); overflow-wrap: anywhere; }
                .tribuna-cabala-state { position: relative; align-self: flex-start; color: #c4b5fd; background: rgba(168,85,247,.13); border: 1px solid rgba(168,85,247,.24); border-radius: 999px; padding: .24rem .5rem; font-size: .64rem; font-weight: 950; text-transform: uppercase; }
                .tribuna-cabala-origin { position: relative; color: rgba(255,255,255,.68); font-size: .72rem; line-height: 1.18; }
                .tribuna-cabala-reactions { position: relative; margin-top: auto; display: grid; grid-template-columns: 1fr; gap: .34rem; color: rgba(255,255,255,.86); font-size: .72rem; font-weight: 900; }
                .tribuna-cabala-reactions button { min-height: 31px; border-radius: 999px; padding: .28rem .46rem; background: rgba(255,255,255,.065); border: 1px solid rgba(255,255,255,.1); color: var(--white); display: flex; align-items: center; justify-content: space-between; gap: .32rem; cursor: pointer; font: inherit; }
                .tribuna-cabala-reactions button:nth-child(1) { border-color: rgba(168,85,247,.22); background: rgba(168,85,247,.095); }
                .tribuna-cabala-reactions button:nth-child(2) { border-color: rgba(245,158,11,.22); background: rgba(245,158,11,.09); }
                .tribuna-cabala-reactions span:last-child { color: #f8fafc; font-size: .75rem; font-weight:950; }
                .tribuna-cabala-react.bump { animation: tribunaCabalaReactBump .34s ease; }
                @keyframes tribunaCabalaReactBump { 0% { transform: scale(1); } 45% { transform: scale(1.04); filter: brightness(1.18); } 100% { transform: scale(1); } }
                .tribuna-cabala-overlay { position: fixed; inset: 0; z-index: 88; background: rgba(0,0,0,.68); backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px); display: flex; align-items: flex-end; justify-content: center; padding: .85rem; overflow-y: auto; }
                .tribuna-cabala-modal { width: min(100%, 440px); max-height: 88vh; overflow-y: auto; padding: 1rem; border-radius: 18px 18px 14px 14px; border: 1px solid rgba(168,85,247,.34); background: radial-gradient(circle at 16% 0%, rgba(168,85,247,.28), transparent 38%), radial-gradient(circle at 100% 16%, rgba(245,158,11,.16), transparent 34%), rgba(9,13,23,.98); box-shadow: 0 24px 70px rgba(0,0,0,.58), 0 0 36px rgba(168,85,247,.18); }
                .tribuna-cabala-fact { min-width: 0; padding: .6rem; border-radius: 12px; background: rgba(255,255,255,.055); border: 1px solid rgba(255,255,255,.09); }
                .tribuna-cabala-fact span { display: block; color: var(--text-muted); font-size: .62rem; font-weight: 900; text-transform: uppercase; }
                .tribuna-cabala-fact strong { display: block; margin-top: .18rem; color: var(--white); font-size: .82rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                @media (min-width: 720px) { .tribuna-cabala-card { flex-basis:190px; width:190px; } .tribuna-cabala-overlay { align-items: center; } }
            </style>
            <!-- DT-EL cerrada (28-may): doc Cábalas v1.0 §13 dice "sin modal en
                 Tribuna". El modal renderCabalaDetail queda definido pero NO se
                 invoca: la reacción ocurre directo en la card del mosaico. -->
            ${''}
            <div style="display:flex;align-items:center;gap:.75rem;margin-bottom:.62rem;">
                <button class="btn btn-secondary" style="padding:.55rem .85rem;margin:0;width:auto;" onclick="window.location.hash='/tribuna/${codigo}'">&larr;</button>
                <div style="min-width:0;">
                    <h2 style="font-family:var(--font-heading);font-size:1.5rem;text-transform:uppercase;color:var(--white);margin:0;">C&aacute;balas activadas</h2>
                    <p style="font-size:.82rem;color:var(--text-muted);margin:.25rem 0 0;line-height:1.28;">Rituales que est&aacute;n corriendo ahora en grupos mundialistas.</p>
                </div>
            </div>
            <!-- 30-may: CTA "Crear mi grupo y activar una cábala" removido (decisión dueño). -->

            ${publicCabalas.length ? `<div class="tribuna-cabala-mosaic">${[0, 1, 2].map(renderCabalaLane).join('')}</div>` : `<div class="glass-card" style="padding:1rem;margin:1rem 0 1.4rem;border-color:rgba(168,85,247,.22);background:rgba(255,255,255,.045);"><strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:1rem;text-transform:uppercase;">Todav&iacute;a no hay c&aacute;balas activadas en la tribuna.</strong><span style="display:block;color:var(--text-muted);font-size:.82rem;line-height:1.35;margin-top:.35rem;">Cuando empiecen los rituales, esto va a arder.</span></div>`}
            <!-- Cartel de gate de reacción UNIFICADO (idéntico a promesa/arenga). -->
            <div id="tribunaCabalaJoinPrompt" style="display:none;position:fixed;inset:0;z-index:92;background:rgba(0,0,0,.68);backdrop-filter:blur(6px);align-items:flex-end;justify-content:center;padding:.85rem;" onclick="if(event.target === this) this.style.display='none'">
                <div class="glass-card" style="width:min(100%,420px);padding:1rem;border-radius:16px 16px 12px 12px;border-color:rgba(168,85,247,.28);background:radial-gradient(circle at 14% 0%,rgba(168,85,247,.18),transparent 38%),rgba(12,18,30,.98);">
                    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:.8rem;margin-bottom:.8rem;">
                        <div>
                            <strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:1.05rem;line-height:1.05;text-transform:uppercase;">Sumate a jugar</strong>
                            <span style="display:block;color:var(--text-muted);font-size:.8rem;line-height:1.32;margin-top:.2rem;">Para reaccionar, entrá a Pacto Mundial.</span>
                        </div>
                        <button class="btn btn-secondary" type="button" style="width:auto;min-height:32px;margin:0;padding:.32rem .58rem;border-radius:999px;" onclick="document.getElementById('tribunaCabalaJoinPrompt').style.display='none'">X</button>
                    </div>
                    <button class="btn btn-primary" type="button" style="margin:0;min-height:46px;padding:.68rem;background:linear-gradient(135deg,var(--celeste),#a855f7);border:none;" onclick="window.location.hash='/'">Entrá a jugar</button>
                </div>
            </div>
        </div>
        ${currentUser ? renderBottomNav(codigo, 'tribuna') : ''}
    `;
    const showCabalaJoinPrompt = () => {
        const prompt = document.getElementById('tribunaCabalaJoinPrompt');
        if (prompt) prompt.style.display = 'flex';
    };
    // DT-EL cerrada (28-may): saqué el handler que navegaba a /tribuna-cabalas/
    // <codigo>/<id> abriendo el modal. La card del mosaico ya tiene los 2 botones
    // de reacción in-place — la card no es clickeable como bloque.
    // Handler tribuna-cabala-react (REESCRITO, auditoría 28-may): optimistic
    // + RPC sin re-render full. Spec Cábalas v1.0 §5: 1 por jugador entre
    // 2 opciones. Si vuelve a clickear la misma → quitar; otra → reemplazo.
    document.querySelectorAll('.tribuna-cabala-react').forEach(button => {
        button.addEventListener('click', async (event) => {
            event.stopPropagation();
            if (!currentUser || !jugadorActivo) {
                showCabalaJoinPrompt();
                return;
            }
            if (button.disabled) return;
            const reaction = button.dataset.reaction;
            const isActive = button.dataset.active === '1';
            const cabalaId = button.dataset.id;
            const prevActive = document.querySelector(`.tribuna-cabala-react[data-id="${cabalaId}"][data-active="1"]`);
            const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null;
            const deltas = computeReactionDelta({ clickedReaction: reaction, isActiveOnClicked: isActive, prevActiveReaction });
            applyReactionDelta({ scope: document, selector: '.tribuna-cabala-react', arengaId: cabalaId, deltas });
            const sumaDeltaCT = deltas.reduce((s, d) => s + d.delta, 0);
            refrescarContadoresRitual('cabala', cabalaId, sumaDeltaCT);
            button.disabled = true;
            button.style.opacity = '.5';
            let r;
            if (isActive) r = await quitarReaccion(jugadorActivo.id, 'cabala', cabalaId);
            else r = await reaccionar(jugadorActivo.id, 'cabala', cabalaId, reaction);
            if (r.error) {
                applyReactionDelta({ scope: document, selector: '.tribuna-cabala-react', arengaId: cabalaId, deltas: invertDeltas(deltas) });
                refrescarContadoresRitual('cabala', cabalaId, -sumaDeltaCT);
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorTribuna(r.error));
                return;
            }
            invalidarCacheTribuna();
            button.disabled = false;
            button.style.opacity = '1';
            document.querySelectorAll(`.tribuna-cabala-react[data-id="${cabalaId}"]`).forEach(b => {
                b.dataset.active = (b === button && !isActive) ? '1' : '0';
            });
        });
    });

    // F1.4.b · Realtime cábalas de la tribuna — auditoría 28-may: skipear
    // re-render full si el cambio es del propio jugador (optimistic ya pintó)
    // o si hay un modal de detalle abierto (causaría flash). El render fresco
    // sale en la próxima visita / al cerrar el modal.
    const refrescar = (payload) => {
        if (!window.location.hash.startsWith('#/tribuna-cabalas/')) return;
        invalidarCacheTribuna();
        const cambioPropio = payload?.new?.player_id && jugadorActivo?.id
            && payload.new.player_id === jugadorActivo.id;
        if (cambioPropio) return;
        if (document.querySelector('.tribuna-cabala-overlay')) return;
        renderTribunaCabalas(codigo);
    };
    subscribirCambios('tribuna-cabalas', 'cabalas', { event: '*' }, refrescar);
    subscribirCambios('tribuna-cabalas', 'reacciones_ritual', { event: 'INSERT' }, refrescar);
};

export const renderTribunaPromesas = async (codigo) => {
    // Promesas activas son contenido público igual que las arengas: el visitante
    // anon ve el feed. Reaccionar adentro pide registro (showJoinPrompt). El
    // feed sale del RPC feed_tribuna (SECURITY DEFINER, GRANT anon), no de una
    // consulta gateada por RLS. Antes (FASE 3.17 · Bloque D) el anon se
    // redirigía a /tribuna — se removió ese guard.
    const now = Date.now();
    const db = getNormalizedDB();
    const grupo = db.grupos[codigo] || { nombre: 'PACTO MUNDIAL' };
    const currentUser = getCurrentUser();
    const jugadorActivo = getMembership(codigo);
    const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

    appRoot.innerHTML = buildTribunaPromesasSkeleton();
    limpiarSuscripciones('tribuna-promesas');

    let feed;
    try { feed = await getFeedTribuna('promesa', { limit: 60 }); }
    catch (err) {
        console.error('[tribuna-promesas] feed falló:', err?.message || err);
        if (!window.location.hash.startsWith('#/tribuna-promesas/')) return;
        appRoot.innerHTML = buildTribunaErrorHTML(codigo, currentUser, 'No se pudieron cargar las promesas');
        appRoot.querySelector('[data-tribuna-retry]')?.addEventListener('click', () => renderTribunaPromesas(codigo));
        return;
    }
    if (!window.location.hash.startsWith('#/tribuna-promesas/')) return;

    const reactionTotal = (p, reaction) => {
        if (reaction === 'sumo') return (p.sumadosGrupo || 0) + (p.sumadosPublicos || 0);
        if (reaction === 'divierte') return (p.divertidosGrupo || 0) + (p.divertidosPublicos || 0);
        if (reaction === 'pasto') return p.pasto || 0;
        return 0;
    };
    // Auditoría 28-may: cargar mi reacción y mis reportes al_pasto para
    // marcar botones activos y bloquear pasto repetido (doc §15).
    const feedPromesaIds = (feed || []).map(p => p.promesa_id);
    const miReaccionPromesaMap = (jugadorActivo?.id && feedPromesaIds.length)
        ? await getMisReacciones(jugadorActivo.id, 'promesa', feedPromesaIds)
        : {};
    const miAlPastoPromesaSet = (jugadorActivo?.id && feedPromesaIds.length)
        ? await getMisAlPasto(jugadorActivo.id, 'promesa', feedPromesaIds)
        : new Set();
    // Feed público de promesas (tribuna_feed) adaptado al shape legacy.
    const publicPromesas = feed.map(p => {
        const reac = p.reacciones || {};
        return {
            id: p.promesa_id,
            condicion: p.condicion,
            compromiso: p.compromiso,
            creadorId: p.creador_id,
            creador: p.creador_nombre || 'Jugador',
            grupoOrigen: p.grupo_nombre || 'Tribuna',
            visibilidad: 'publica',
            estado: p.estado === 'en_extension' ? 'extension' : p.estado,
            createdAt: new Date(p.created_at).getTime(),
            deadlineAt: new Date(p.deadline_at).getTime(),
            sumadosPublicos: reac.sumo || 0,
            divertidosPublicos: reac.divierte || 0,
            pasto: reac.al_pasto || 0,
            miReaccion: miReaccionPromesaMap[p.promesa_id] || null,
            miAlPasto: miAlPastoPromesaSet.has(p.promesa_id),
            aprobacionPublica: 'aprobada',
        };
    });
    // TODO 3.12-followup: extraer como formatRemainingCabala / formatRemainingPromesa
    // al nivel del módulo. Hoy son 2 declaraciones locales con misma lógica matemática
    // pero distinto texto de output ('quedan X' vs 'X restantes'). Diferido a sub-fase
    // dedicada de limpieza interna de tribuna.js.
    const formatRemaining = (deadlineAt) => {
        const minutes = Math.ceil(Math.max(0, deadlineAt - Date.now()) / 60000);
        if (minutes < 60) return `${minutes} min restantes`;
        const hours = Math.ceil(minutes / 60);
        if (hours < 24) return `${hours} h restantes`;
        const days = Math.ceil(hours / 24);
        return days === 1 ? '1 dia restante' : `${days} dias restantes`;
    };
    const isOwnPromesaPub = (p) => jugadorActivo?.id && p.creadorId
        && jugadorActivo.id.toString() === p.creadorId.toString();
    const renderPromiseCard = (promesa, index) => {
        const tilt = [-0.8, 0.6, -0.3, 0.9][index % 4];
        const minePub = isOwnPromesaPub(promesa);
        const btn = (reactId, label) => {
            const active = !minePub && promesa.miReaccion === reactId;
            const cls = minePub ? '' : ' tribuna-promesa-react';
            return `<button type="button" class="${cls.trim()}" data-id="${promesa.id}" data-reaction="${reactId}" data-active="${active ? '1' : '0'}" ${minePub ? 'aria-disabled="true" style="opacity:.55;cursor:default;"' : (active ? 'style="background:rgba(245,158,11,.22);border-color:rgba(245,158,11,.55);"' : '')}><span>${label}</span> <span data-promesa-count="${promesa.id}:${reactId}">${reactionTotal(promesa, reactId)}</span></button>`;
        };
        const pastoBtn = () => {
            if (minePub) {
                return `<button type="button" aria-disabled="true" style="opacity:.55;cursor:default;">&#129406; Al pasto <b>${reactionTotal(promesa, 'pasto')}</b></button>`;
            }
            const active = promesa.miAlPasto;
            const label = active ? '✓ Reportado' : '&#129406; Al pasto';
            return `<button type="button" class="tribuna-promesa-react" data-id="${promesa.id}" data-reaction="pasto" data-active="${active ? '1' : '0'}" ${active ? 'style="background:rgba(192,132,252,.22);border-color:rgba(192,132,252,.55);"' : ''}><span>${label}</span> <span data-promesa-count="${promesa.id}:pasto">${reactionTotal(promesa, 'pasto')}</span></button>`;
        };
        return `
            <article class="tribuna-promesa-card" style="animation-delay:${index * 0.08}s;transform:rotate(${tilt}deg);">
                <div class="tribuna-promesa-top"><span>ACTIVA</span><b>&#9203; ${formatRemaining(promesa.deadlineAt)}</b></div>
                <div class="tribuna-promesa-block"><span>SI SUCEDE...</span><strong>${escapeHTML(promesa.condicion || 'Pasa algo historico')}</strong></div>
                <div class="tribuna-promesa-block commitment"><span>ME COMPROMETO A...</span><strong>${escapeHTML(promesa.compromiso || 'cumplir una promesa fuerte')}</strong></div>
                <div class="tribuna-promesa-meta">&#128100; ${escapeHTML(promesa.creador || 'Jugador')} &middot; &#127968; ${escapeHTML((promesa.grupoOrigen || grupo.nombre).toUpperCase())} &middot; &#127758; P&uacute;blica${minePub ? ' &middot; tu promesa' : ''}</div>
                ${(() => { const t = (promesa.sumadosPublicos||0)+(promesa.divertidosPublicos||0)+(promesa.pasto||0); const p = puntosPorRitual({ reaccionesTotal: t, esIncumplida: promesa.estado === 'incumplida' }); return `<div data-rit-pts="promesa:${promesa.id}" data-rit-reac="${t}" data-rit-incumplida="${promesa.estado === 'incumplida' ? '1' : '0'}" style="margin:.18rem 0 .32rem;color:${p>=0?'#7ab488':'#c47171'};font-size:.6rem;">Puntos: ${p>0?'+':''}${p}</div>`; })()}
                <div class="tribuna-promesa-reactions">
                    ${btn('sumo', '&#128293; Me sumo')}
                    ${btn('divierte', '&#128514; Jua')}
                    ${pastoBtn()}
                </div>
            </article>
        `;
    };
    const renderPromesaLane = (laneIndex) => {
        const laneItems = [...publicPromesas]
            .sort((a, b) => ((a.id + laneIndex) > (b.id + laneIndex) ? 1 : -1))
            .map((promesa, index) => renderPromiseCard(promesa, index + laneIndex));
        const reverse = laneIndex % 2 === 1;
        // 30-may: acelerado de 98+14k → 70+10k (un escalón más rápido,
        // menos agresivo que cábalas). El dueño lo reportó algo lento.
        const duration = 70 + (laneIndex * 10);
        return `<div class="tribuna-promesa-lane" style="--duration:${duration}s;"><div class="tribuna-promesas-track ${reverse ? 'reverse' : ''}">${[laneItems.join(''), laneItems.join('')].join('')}</div></div>`;
    };
    appRoot.innerHTML = `
        <div class="view-enter" style="width:100%;animation:fadeIn .4s ease-out forwards;">
            <style>
                @keyframes tribunaPromesaIn { from { opacity:0; transform:translateY(12px) scale(.985); } to { opacity:1; } }
                @keyframes tribunaPromesaGlow { 0%,100% { filter:brightness(1); } 50% { filter:brightness(1.06); } }
                @keyframes tribunaPromesaReactBump { 0% { transform:scale(1); } 45% { transform:scale(1.05); filter:brightness(1.16); } 100% { transform:scale(1); } }
                @keyframes tribunaPromesaMarquee { from { transform:translate3d(0,0,0); } to { transform:translate3d(-50%,0,0); } }
                @keyframes tribunaPromesaMarqueeReverse { from { transform:translate3d(-50%,0,0); } to { transform:translate3d(0,0,0); } }
                .tribuna-promesas-list { overflow:hidden; margin:.62rem -1rem 1rem; padding:.12rem 0 .28rem; mask-image:linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%); display:grid; gap:.48rem; }
                .tribuna-promesa-lane { overflow:hidden; padding:0 1rem; }
                .tribuna-promesas-track { display:flex; align-items:stretch; gap:.48rem; width:max-content; will-change:transform; animation:tribunaPromesaMarquee 70s linear infinite; }
                .tribuna-promesas-track.reverse { animation-name:tribunaPromesaMarqueeReverse; }
                .tribuna-promesas-track:hover, .tribuna-promesas-track:focus-within { animation-play-state:paused; }
                .tribuna-promesa-card { flex:0 0 min(78vw, 282px); width:min(78vw, 282px); border:1px solid rgba(245,158,11,.22); border-radius:13px; padding:.52rem .56rem; background:radial-gradient(circle at 10% 0%, rgba(245,158,11,.16), transparent 38%), radial-gradient(circle at 96% 100%, rgba(239,68,68,.1), transparent 36%), linear-gradient(145deg, rgba(17,24,39,.97), rgba(10,14,24,.98)); box-shadow:0 10px 24px rgba(0,0,0,.3), 0 0 18px rgba(245,158,11,.08), inset 0 1px 0 rgba(255,255,255,.07); animation:tribunaPromesaIn .42s ease both, tribunaPromesaGlow 5s ease-in-out infinite; transition:transform .3s ease; pointer-events:auto; }
                .tribuna-promesa-top { display:flex;align-items:center;justify-content:space-between;gap:.42rem;margin-bottom:.38rem; }
                .tribuna-promesa-top span { color:#06111f;background:linear-gradient(135deg,#fde68a,#f59e0b);border-radius:999px;padding:.17rem .42rem;font-size:.56rem;font-weight:950; }
                .tribuna-promesa-top b { color:#fde68a;font-size:.6rem;white-space:nowrap; }
                .tribuna-promesa-block { padding:.42rem .5rem;border-radius:10px;background:rgba(0,0,0,.18);border:1px solid rgba(255,255,255,.085);margin-bottom:.32rem; }
                .tribuna-promesa-block.commitment { background:rgba(245,158,11,.09);border-color:rgba(245,158,11,.18); }
                .tribuna-promesa-block span { display:block;color:#fcd34d;font-size:.52rem;font-weight:950;text-transform:uppercase;line-height:1; }
                .tribuna-promesa-block strong { display:block;color:var(--white);font-size:.82rem;line-height:1.08;margin-top:.1rem;overflow-wrap:anywhere;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden; }
                .tribuna-promesa-meta { color:rgba(255,255,255,.62);font-size:.62rem;line-height:1.1;margin:.34rem 0 .42rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
                .tribuna-promesa-reactions { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.24rem; }
                .tribuna-promesa-reactions button { min-height:30px;border-radius:999px;border:1px solid rgba(255,255,255,.095);background:rgba(255,255,255,.052);color:var(--white);font-size:.58rem;font-weight:900;display:flex;align-items:center;justify-content:center;gap:.14rem;padding:.22rem .16rem;cursor:pointer;white-space:nowrap; }
                .tribuna-promesa-reactions span:last-child { color:#f8fafc;font-size:.58rem;font-weight:900; }
                .tribuna-promesa-react.bump { animation:tribunaPromesaReactBump .34s ease; }
            </style>
            <div style="display:flex;align-items:center;gap:.58rem;margin-bottom:.55rem;">
                <button class="btn btn-secondary" style="padding:.42rem .7rem;margin:0;width:auto;min-height:34px;" onclick="window.location.hash='/tribuna/${codigo}'">&larr;</button>
                <div style="min-width:0;">
                    <h2 style="font-family:var(--font-heading);font-size:1.22rem;text-transform:uppercase;color:var(--white);margin:0;line-height:1;">Promesas p&uacute;blicas</h2>
                    <p style="font-size:.7rem;color:var(--text-muted);margin:.16rem 0 0;line-height:1.16;">Apuestas de honor corriendo ahora en grupos mundialistas.</p>
                </div>
            </div>
            <!-- 30-may: CTA "Crear mi grupo y hacer una promesa" removido (decisión dueño). -->

            ${publicPromesas.length ? `<div class="tribuna-promesas-list">${[0, 1, 2].map(renderPromesaLane).join('')}</div>` : `<div class="glass-card" style="padding:1rem;margin:1rem 0 1.4rem;border-color:rgba(245,158,11,.22);background:rgba(255,255,255,.045);"><strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:1rem;text-transform:uppercase;">Todav&iacute;a no hay promesas p&uacute;blicas activas.</strong><span style="display:block;color:var(--text-muted);font-size:.82rem;line-height:1.35;margin-top:.35rem;">Cuando empiecen las apuestas de honor, esto va a arder.</span></div>`}
            <!-- Cartel de gate de reacción UNIFICADO (idéntico a cábala/arenga). -->
            <div id="tribunaPromesaJoinPrompt" style="display:none;position:fixed;inset:0;z-index:92;background:rgba(0,0,0,.68);backdrop-filter:blur(6px);align-items:flex-end;justify-content:center;padding:.85rem;" onclick="if(event.target === this) this.style.display='none'">
                <div class="glass-card" style="width:min(100%,420px);padding:1rem;border-radius:16px 16px 12px 12px;border-color:rgba(168,85,247,.28);background:radial-gradient(circle at 14% 0%,rgba(168,85,247,.18),transparent 38%),rgba(12,18,30,.98);">
                    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:.8rem;margin-bottom:.8rem;">
                        <div>
                            <strong style="display:block;color:var(--white);font-family:var(--font-heading);font-size:1.05rem;line-height:1.05;text-transform:uppercase;">Sumate a jugar</strong>
                            <span style="display:block;color:var(--text-muted);font-size:.8rem;line-height:1.32;margin-top:.2rem;">Para reaccionar, entrá a Pacto Mundial.</span>
                        </div>
                        <button class="btn btn-secondary" type="button" style="width:auto;min-height:32px;margin:0;padding:.32rem .58rem;border-radius:999px;" onclick="document.getElementById('tribunaPromesaJoinPrompt').style.display='none'">X</button>
                    </div>
                    <button class="btn btn-primary" type="button" style="margin:0;min-height:46px;padding:.68rem;background:linear-gradient(135deg,var(--celeste),#a855f7);border:none;" onclick="window.location.hash='/'">Entrá a jugar</button>
                </div>
            </div>
        </div>
        ${currentUser ? renderBottomNav(codigo, 'tribuna') : ''}
    `;
    const showJoinPrompt = () => {
        const prompt = document.getElementById('tribunaPromesaJoinPrompt');
        if (prompt) prompt.style.display = 'flex';
    };
    // Handler tribuna-promesa-react (REESCRITO, auditoría 28-may): optimistic
    // + bloqueo Al pasto si ya reporté (doc §15) + sin re-render full.
    document.querySelectorAll('.tribuna-promesa-react').forEach(button => {
        button.addEventListener('click', async () => {
            if (!currentUser || !jugadorActivo) {
                showJoinPrompt();
                return;
            }
            if (button.disabled) return;
            const reaction = button.dataset.reaction;
            const isActive = button.dataset.active === '1';
            const promesaId = button.dataset.id;
            if (reaction === 'pasto' && isActive) return;
            const prevActive = document.querySelector(`.tribuna-promesa-react[data-id="${promesaId}"][data-active="1"]`);
            const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null;
            const deltas = computeReactionDelta({ clickedReaction: reaction, isActiveOnClicked: isActive, prevActiveReaction });
            applyReactionDelta({ scope: document, selector: '.tribuna-promesa-react', arengaId: promesaId, deltas });
            const sumaDeltaPT = deltas.reduce((s, d) => s + d.delta, 0);
            refrescarContadoresRitual('promesa', promesaId, sumaDeltaPT);
            button.disabled = true;
            button.style.opacity = '.5';
            const tipoReaccion = reaction === 'pasto' ? 'al_pasto' : reaction;
            let r;
            if (reaction === 'pasto') r = await reaccionar(jugadorActivo.id, 'promesa', promesaId, 'al_pasto');
            else if (isActive) r = await quitarReaccion(jugadorActivo.id, 'promesa', promesaId);
            else r = await reaccionar(jugadorActivo.id, 'promesa', promesaId, tipoReaccion);
            if (r.error) {
                applyReactionDelta({ scope: document, selector: '.tribuna-promesa-react', arengaId: promesaId, deltas: invertDeltas(deltas) });
                refrescarContadoresRitual('promesa', promesaId, -sumaDeltaPT);
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorTribuna(r.error));
                return;
            }
            invalidarCacheTribuna();
            button.disabled = false;
            button.style.opacity = '1';
            if (reaction === 'pasto') {
                button.dataset.active = '1';
                button.style.background = 'rgba(192,132,252,0.22)';
                button.style.borderColor = 'rgba(192,132,252,0.55)';
                button.innerHTML = button.innerHTML.replace(/&#129406; Al pasto/, '✓ Reportado');
            } else {
                document.querySelectorAll(`.tribuna-promesa-react[data-id="${promesaId}"]`).forEach(b => {
                    if (b.dataset.reaction === 'pasto') return;
                    b.dataset.active = (b === button && !isActive) ? '1' : '0';
                });
            }
        });
    });

    // F1.4.b · Realtime promesas de la tribuna — auditoría 28-may: skipear
    // re-render full si el cambio es propio o si hay modal abierto (mismo
    // patrón que arengas/cábalas: optimistic local cubre el caso normal).
    const refrescar = (payload) => {
        if (!window.location.hash.startsWith('#/tribuna-promesas/')) return;
        invalidarCacheTribuna();
        const cambioPropio = payload?.new?.player_id && jugadorActivo?.id
            && payload.new.player_id === jugadorActivo.id;
        if (cambioPropio) return;
        renderTribunaPromesas(codigo);
    };
    subscribirCambios('tribuna-promesas', 'promesas', { event: '*' }, refrescar);
    subscribirCambios('tribuna-promesas', 'reacciones_ritual', { event: 'INSERT' }, refrescar);
};

// ─── FASE 3.17 · Bloque D — link viral /t/AUDIO_ID ────────────────────────

// Render de la pantalla "esta arenga no se puede mostrar" — caso E1 (privada
// inaccesible) y caso "no_existe". Diferencia el texto según motivo + sesión.
const renderMensajeAccesoArenga = (motivo) => {
    const currentUser = getCurrentUser();
    const titulo = motivo === 'no_existe' ? 'Arenga no encontrada' : 'Esta arenga es privada';
    const mensaje = motivo === 'no_existe'
        ? 'No encontramos esta arenga. Puede haber sido eliminada por su creador.'
        : currentUser
            ? 'Esta arenga es de un grupo al que no pertenecés.'
            : 'Esta arenga es de un grupo privado. Iniciá sesión para ver si tenés acceso.';
    const cta = currentUser
        ? `<button class="btn btn-primary" type="button" style="margin:1rem 0 0;min-height:46px;padding:.68rem;background:linear-gradient(135deg,var(--celeste),#a855f7);border:none;" onclick="window.location.hash='/'">Volver al inicio</button>`
        : `<button class="btn btn-primary" type="button" style="margin:1rem 0 0;min-height:46px;padding:.68rem;background:linear-gradient(135deg,var(--celeste),#a855f7);border:none;" onclick="window.location.hash='/'">Iniciá sesión</button>
           <button class="btn btn-secondary" type="button" style="margin:.5rem 0 0;min-height:42px;padding:.62rem;background:rgba(255,255,255,.045);" onclick="window.location.hash='/tribuna/PUBLICO'">Ver Tribuna Pública</button>`;
    appRoot.innerHTML = `
        <div class="view-enter glass-card" style="width:min(100%,440px);margin:3rem auto;padding:1.5rem;text-align:center;">
            <div style="font-size:2.5rem;margin-bottom:.5rem;">🔒</div>
            <h2 style="font-family:var(--font-heading);font-size:1.2rem;color:var(--white);text-transform:uppercase;margin:0 0 .5rem;">${titulo}</h2>
            <p style="color:var(--text-muted);font-size:.85rem;line-height:1.4;margin:0;">${mensaje}</p>
            ${cta}
        </div>
    `;
};

// renderTribunaPublica(audioId?) — entry-point doble:
//   - SIN audioId → tribuna entera pública (link /tribuna). Modo visitante
//     puro si no hay sesión; bottom-nav usa el grupo activo si existe, o
//     placeholder PUBLICO si no.
//   - CON audioId → link viral /t/AUDIO_ID. Resuelve si la arenga es accesible
//     y si lo es, renderTribuna con la arenga destacada. Si no, mensaje.
export const renderTribunaPublica = async (audioId) => {
    const codigo = getActiveGroupCode() || 'PUBLICO';
    if (!audioId) {
        // Tribuna entera pública: sin arenga destacada, todo el feed.
        await renderTribuna(codigo, {});
        return;
    }
    appRoot.innerHTML = buildTribunaSkeleton();
    const currentUser = getCurrentUser();
    const r = await resolverRutaTribunaPublica(audioId, currentUser);
    if (!r.accesible) {
        renderMensajeAccesoArenga(r.motivo);
        return;
    }
    await renderTribuna(codigo, { destacarArenga: r.arenga });
};

if (typeof window !== 'undefined') {
  window.renderTribuna = renderTribuna;
  window.renderTribunaArengas = renderTribunaArengas;
  window.renderTribunaCabalas = renderTribunaCabalas;
  window.renderTribunaPromesas = renderTribunaPromesas;
  window.renderTribunaPublica = renderTribunaPublica;
}
